/* ══════════════════════════════════════════════════════════
   BELT BOX BUILDER  —  belt-box-builder.js
   ──────────────────────────────────────────────────────────
   All template-specific JS extracted from builders.liquid.
   Liquid-rendered data is supplied via window.BSB_DATA
   (a small inline <script> in builders.liquid sets this
   before this file is loaded with defer).

   Sections:
     1.  Data bridge   — read window.BSB_DATA
     2.  VARIANT_MAP   — static variant table
     3.  ALL_PRODUCTS  — normalised product array
     4.  Globals       — shared state variables
     5.  Toast system
     6.  Sticky bar
     7.  Add-to-cart state & add-on system
     8.  Variant resolution
     9.  handleAtcClick
    10.  Dawn cart drawer opener
    11.  FilterManager base class
    12.  StrapFilter  (extends FilterManager)
    13.  BuckleFilter (extends FilterManager)
    14.  Width / step flow
    15.  Toggle helpers
    16.  Sidebar
    17.  Upgrade modal
    18.  Combinations & carousel
    19.  Lightbox
    20.  Zoom / gallery modal
    21.  Auto-scroll engine
    22.  Product-form submission guard
    23.  Global event listeners
══════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════
   1. DATA BRIDGE — read Liquid-rendered values
══════════════════════════════════════════════════════════ */
const _BSB_D        = window.BSB_DATA        || {};
const BOX_PRICE             = _BSB_D.BOX_PRICE             || 9900;
const BOX_COMPARE_PRICE     = _BSB_D.BOX_COMPARE_PRICE     || 0;
/* Manual addon overrides from section settings (product IDs → price in cents).
   Liquid renders addon status directly on each card via data-is-addon, but this
   map allows manual overrides for edge cases that Liquid cannot auto-detect. */
const ADDON_META_EXTRA = _BSB_D.ADDON_META_EXTRA || {};
const ADDON_CART_VID        = _BSB_D.ADDON_CART_VID        || 0;
const VARIANT_PRICE_MAP         = _BSB_D.VARIANT_PRICE_MAP         || {};
const VARIANT_COMPARE_PRICE_MAP = _BSB_D.VARIANT_COMPARE_PRICE_MAP || {};
/* Selection-key price table — prices matched to VARIANT_MAP entries.
   Used as a reliable fallback when SHOPIFY_VARIANTS is empty (no box_product
   configured in theme settings) and VARIANT_PRICE_MAP is not set.
   Can be overridden via window.BSB_DATA.SELECTION_PRICE_MAP. */
const SELECTION_PRICE_MAP = _BSB_D.SELECTION_PRICE_MAP || {
  '5_0': 9999,  '5_1': 10399, '5_2': 10799, '5_3': 11199, '5_4': 11599, '5_5': 11999,
  '6_0': 11498, '6_1': 11898, '6_2': 12298, '6_3': 12698, '6_4': 13098, '6_5': 13498, '6_6': 13898
};
/* Real Shopify variant prices from the product selected in the theme editor.
   Format: { "variantId": { price: <cents>, compare_at_price: <cents|0> } }
   This is the primary price source — overrides VARIANT_PRICE_MAP and BOX_PRICE. */
const SHOPIFY_VARIANTS          = _BSB_D.SHOPIFY_VARIANTS          || {};
/* Women's builder: one fixed width, so there is no "Choose Belt Width" step.
   BSB_DATA.FIXED_WIDTH (e.g. "1.25") is applied on load and every later step is numbered one lower. */
const FIXED_WIDTH = _BSB_D.FIXED_WIDTH ? String(_BSB_D.FIXED_WIDTH) : null;
function _stepNo(n) { return FIXED_WIDTH ? n - 1 : n; }

/* ══════════════════════════════════════════════════════════
   2. STATIC VARIANT MAP
   Key format : "totalItems_addonCount"  (e.g. "6_3")
   Value      : Shopify variant ID string
   Rules:
     ✅ variant resolved ONLY from this object
     ❌ no fallback to Shopify's default/first variant
     ❌ if key absent → ATC blocked (never silently wrong)
══════════════════════════════════════════════════════════ */
/* A page can supply its own table (women's box set) via BSB_DATA.VARIANT_MAP. */
const VARIANT_MAP = Object.freeze(_BSB_D.VARIANT_MAP || {
  /* ── 5-item variants (product_id: 124255248) ── */
  '5_0': '12664835735637',   // "5 Items"                          $99.99
  '5_1': '42272931119189',   // "5 Items; Premium upgrades: 1"    $103.99
  '5_2': '42272931151957',   // "5 Items; Premium upgrades: 2"    $107.99
  '5_3': '42272931184725',   // "5 Items; Premium upgrades: 3"    $111.99
  '5_4': '42272931217493',   // "5 Items; Premium upgrades: 4"    $115.99
  '5_5': '42272931250261',   // "5 Items; Premium upgrades: 5"    $119.99
  /* ── 6-item variants ── */
  '6_0': '12681231663189',   // "6 Items"                         $114.98
  '6_1': '42272930922581',   // "6 Items; Premium upgrades: 1"    $118.98
  '6_2': '42272930955349',   // "6 Items; Premium upgrades: 2"    $122.98
  '6_3': '42272930988117',   // "6 Items; Premium upgrades: 3"    $126.98
  '6_4': '42272931020885',   // "6 Items; Premium upgrades: 4"    $130.98
  '6_5': '42272931053653',   // "6 Items; Premium upgrades: 5"    $134.98
  '6_6': '42272931086421'    // "6 Items; Premium upgrades: 6"    $138.98
});
console.log('[BSB] VARIANT_MAP loaded —', Object.keys(VARIANT_MAP).length,
            'keys:', Object.keys(VARIANT_MAP).join(', '));

/* ══════════════════════════════════════════════════════════
   3. ALL_PRODUCTS — built from Liquid-rendered DOM cards
   Each product card in productsGrid / productsGridBuckles
   carries data-* attributes set by the Liquid template.
   This replaces the old JSON+AJAX approach entirely.
══════════════════════════════════════════════════════════ */
let ALL_PRODUCTS = [];
(function _buildProductsFromDOM() {
  function _parseTags(val) {
    return val ? val.split(',').map(function(t) { return t.trim(); }).filter(Boolean) : [];
  }
  function _parseImages(val) {
    if (!val) return [];
    try { return JSON.parse(val); } catch(e) { return []; }
  }
  /* Fallback 1: extract size from data-tags CSV — looks for "size_1-25" → "1.25" */
  function _sizeFromTags(tagsStr) {
    if (!tagsStr) return '';
    var tags = tagsStr.split(',');
    for (var i = 0; i < tags.length; i++) {
      var t = tags[i].trim().toLowerCase();
      if (t.startsWith('size_')) {
        var n = parseFloat(t.slice(5).replace(/-/g, '.'));
        if (!isNaN(n)) return String(n);
      }
    }
    return '';
  }
  /* Fallback 2: extract size from handle when tag-based size is absent.
     Handles like "1-25-black-leather" or "1-5-casual" start with the size slug. */
  function _sizeFromHandle(handle) {
    if (!handle) return '';
    var m = handle.match(/^(\d+-\d+)-/);
    if (!m) return '';
    var n = parseFloat(m[1].replace(/-/g, '.'));
    return isNaN(n) ? '' : String(n);
  }

  document.querySelectorAll('[data-product-type="strap"]').forEach(function(card) {
    var size = card.dataset.size || _sizeFromTags(card.dataset.tags) || _sizeFromHandle(card.dataset.handle);
    ALL_PRODUCTS.push({
      id:      parseInt(card.dataset.productId, 10),
      title:   card.dataset.title   || '',
      handle:  card.dataset.handle  || '',
      price:   parseInt(card.dataset.price, 10) || 0,
      isAddon: card.dataset.isAddon === 'true',
      size:    size,
      tags:    _parseTags(card.dataset.tags),
      image:   card.dataset.image   || '',
      images:  _parseImages(card.dataset.images),
      type:    'strap',
      sku:     card.dataset.sku     || ''
    });
  });

  document.querySelectorAll('[data-product-type="buckle"]').forEach(function(card) {
    var size = card.dataset.size || _sizeFromTags(card.dataset.tags) || _sizeFromHandle(card.dataset.handle);
    ALL_PRODUCTS.push({
      id:      'b' + card.dataset.productId,
      title:   card.dataset.title   || '',
      handle:  card.dataset.handle  || '',
      price:   parseInt(card.dataset.price, 10) || 0,
      isAddon: false,
      size:    size,
      tags:    _parseTags(card.dataset.tags),
      image:   card.dataset.image   || '',
      images:  _parseImages(card.dataset.images),
      type:    'buckle',
      sku:     card.dataset.sku     || ''
    });
  });

  console.log('[BSB] DOM scan —', ALL_PRODUCTS.length, 'products found.',
    'Straps:', ALL_PRODUCTS.filter(function(p){ return p.type==='strap'; }).length,
    'Buckles:', ALL_PRODUCTS.filter(function(p){ return p.type==='buckle'; }).length,
    '| Sizes:', [...new Set(ALL_PRODUCTS.map(function(p){ return p.size; }))].join(', '));
  /* Debug: show first strap's tags so you can verify size tag format in browser console */
  var _firstStrap = ALL_PRODUCTS.find(function(p){ return p.type==='strap'; });
  if (_firstStrap) console.log('[BSB] First strap tags:', _firstStrap.tags, '| size field:', _firstStrap.size, '| handle:', _firstStrap.handle);

  var _noSize = ALL_PRODUCTS.filter(function(p){ return !p.size; });
  if (_noSize.length) {
    console.warn('[BSB] ' + _noSize.length + ' product(s) have no size tag — add a "size_1-25" or "size_1-5" tag in Shopify admin.',
      _noSize.map(function(p){ return p.handle || p.id; }));
  }

  /* Apply manual addon overrides from ADDON_META_EXTRA section setting */
  Object.keys(ADDON_META_EXTRA).forEach(function(idStr) {
    var numId = parseInt(idStr, 10);
    var p = ALL_PRODUCTS.find(function(x) { return x.type === 'strap' && x.id === numId; });
    if (p) {
      p.isAddon = true;
      var price = ADDON_META_EXTRA[idStr];
      if (typeof price === 'number' && price > 0) p.price = price;
    }
  });
})();

/**
 * Fade out the grid loading overlay once visible card images have loaded.
 * Only watches images inside currently-visible (.product-card not display:none)
 * cards — hidden cards from other sizes/filters are excluded.
 * Falls back after 1200ms so a slow network never blocks the grid.
 */
function _bsbHideLoaderWhenReady(loader, gridEl) {
  var MIN_MS   = 350;
  var resolved = false;

  function doHide() {
    if (resolved) return;
    resolved = true;
    clearTimeout(loader._bsbHideTimer);
    var elapsed = performance.now() - (loader._bsbShowTime || 0);
    var wait    = Math.max(0, MIN_MS - elapsed);
    loader._bsbHideTimer = setTimeout(function() {
      loader.style.transition    = 'opacity 0.25s ease';
      loader.style.opacity       = '0';
      setTimeout(function() {
        loader.style.pointerEvents = 'none';
        loader.style.transition    = '';
      }, 300);
    }, wait);
  }

  /* Only consider images in visible cards (Liquid pre-renders all cards hidden;
     renderProducts shows the matching subset). */
  var imgs = Array.from(gridEl.querySelectorAll('.product-card img')).filter(function(img) {
    var card = img.closest('.product-card');
    return card && card.style.display !== 'none';
  });
  if (!imgs.length) { doHide(); return; }

  var threshold = Math.min(4, imgs.length);
  var loaded    = 0;
  var _fallback = setTimeout(doHide, 1200);

  imgs.forEach(function(img) {
    if (img.complete && img.naturalWidth > 0) {
      loaded++;
      if (loaded >= threshold) { clearTimeout(_fallback); doHide(); }
    } else {
      img.addEventListener('load', function() {
        loaded++;
        if (loaded >= threshold) { clearTimeout(_fallback); doHide(); }
      }, { once: true });
      img.addEventListener('error', function() {
        loaded++;
        if (loaded >= threshold) { clearTimeout(_fallback); doHide(); }
      }, { once: true });
    }
  });
}

/* ══════════════════════════════════════════════════════════
   4. GLOBALS — shared mutable state
══════════════════════════════════════════════════════════ */
const MAX_STRAPS  = 3;
let   MAX_BUCKLES = 2;
let   selectedSize = null;
let   pendingWidth = null;
let   hasUpgraded = false;
let   upgradeOffered = false;
let   userDeclinedUpgrade = false;
let   carouselIndex = 0;
let   totalCombos = 0;
let   combosData    = [];
let   _cartUpdateInProgress = false;
let   _bsbLockedVid = 0;
let   _bsbLockedKey = '';
let   _pendingAtcBtn = null;
/* Scroll Y captured when upgrade modal opens — restored on accept/decline
   to prevent iOS Safari momentum scroll from drifting the page. */
var   _upgradeScrollY = 0;

/* ── Scroll-lock helpers ─────────────────────────────────
   All modal open/close functions route through these two helpers.
   Unlock only restores scroll after confirming every overlay is closed. */
var _BSB_ANY_OPEN = '#upgradeModal.is-open, #premiumModal.is-open, #mixWidthsModal.is-open, ' +
                    '#klarnaModal.is-open, #zoomModal.is-open, ' +
                    '#filterPanel.is-open, #filterPanelBuckles.is-open, .strap-tooltip.is-open';
function _bsbLockScroll() {
  document.documentElement.classList.add('bsb-scroll-locked');
}
function _bsbUnlockScroll() {
  if (!document.querySelector(_BSB_ANY_OPEN)) {
    document.documentElement.classList.remove('bsb-scroll-locked');
    document.body.style.overflow = '';
  }
}

/* ══════════════════════════════════════════════════════════
   5. TOAST SYSTEM
══════════════════════════════════════════════════════════ */
function showToast(message, type = 'success', icon = '') {
  const icons = { success: '✅', remove: '✅', info: '✅', warning: '✅' };
  const container = document.getElementById('bsbToastContainer');
  const toast = document.createElement('div');
  toast.className = `bsb-toast bsb-toast--${type}`;
  toast.innerHTML = `<span class="bsb-toast__icon">${icon || icons[type]}</span><span>${message}</span>`;
  container.appendChild(toast);
  setTimeout(() => {
    toast.classList.add('toast-out');
    setTimeout(() => toast.remove(), 320);
  }, 2800);
}

/* ══════════════════════════════════════════════════════════
   6. STICKY BAR
   Bar starts in normal document flow at the bottom of the
   hero banner (HTML lives in the template, outside .page-width).
   Once the page is scrolled past the bar's natural position
   we fix it to the top of the viewport and insert a spacer
   so the layout does not jump.
══════════════════════════════════════════════════════════ */
(function initStickyBar() {
  var bar = document.getElementById('bsbStickyBar');
  if (!bar) return;

  bar.style.position = '';  /* let CSS handle it */

  function syncHeight() {
    var h = bar.offsetHeight;
    document.documentElement.style.setProperty('--sticky-bar-height', h + 'px');
  }
  syncHeight();

  var sentinel = document.getElementById('bsbStickyBarSentinel');
  if (!sentinel) {
    sentinel = document.createElement('div');
    sentinel.id = 'bsbStickyBarSentinel';
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
    bar.parentNode.insertBefore(sentinel, bar);
  }

  if (typeof IntersectionObserver !== 'undefined') {
    new IntersectionObserver(function(entries) {
      entries.forEach(function(e) {
        bar.classList.toggle('is-sticky', !e.isIntersecting);
      });
    }, { threshold: 0, rootMargin: '0px' }).observe(sentinel);
  }

  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(syncHeight).observe(bar);
  }
})();

function updateStickySteps(activeStep) {
  [1,2,3,4].forEach(n => {
    const el = document.getElementById('stickyStep' + n);
    if (!el) return;
    el.classList.remove('is-active', 'is-done');
    if (n < activeStep) el.classList.add('is-done');
    else if (n === activeStep) {
      /* Step 4 (Preview) is the last step — there's nothing further for it to
         hand off to, so once reached it should read as complete (green) like
         1-3 do, not sit in the transient "current step" (purple) look. */
      el.classList.add(n === 4 ? 'is-done' : 'is-active');
    }
  });
}

function updateStickyMsg(msg) {
  const el = document.getElementById('stickyCtaText');
  if (el) el.textContent = msg;
  const mob = document.getElementById('mobileBarStatus');
  if (mob) mob.textContent = msg;
}

/* ══════════════════════════════════════════════════════════
   7. ADD-TO-CART STATE & ADD-ON SYSTEM
══════════════════════════════════════════════════════════ */
function checkAllDone() {
  const strapsOk  = filterManagers.strap.selectedStraps.length === MAX_STRAPS;
  const bucklesOk = filterManagers.buckle.selectedBuckles.length === MAX_BUCKLES;
  const done = strapsOk && bucklesOk;

  const hasVariant = done ? !!resolveVariantId() : false;
  const ready = done && hasVariant;

  ['sidebarAtcBtn','stickyAtcBtn','mobileAtcBtn','finalAtcBtn'].forEach(id => {
    const btn = document.getElementById(id);
    if (!btn) return;
    btn.disabled = !ready;
    btn.classList.toggle('is-ready', ready);
  });

  const hint = document.getElementById('atcHint');
  if (hint) {
    if (done) {
      hint.textContent = 'You\'re all set!';
      hint.style.color = '#27ae60';
    } else {
      const need = [];
      if (!strapsOk)  need.push(`${MAX_STRAPS - filterManagers.strap.selectedStraps.length} strap${MAX_STRAPS - filterManagers.strap.selectedStraps.length !== 1 ? 's' : ''}`);
      if (!bucklesOk) need.push(`${MAX_BUCKLES - filterManagers.buckle.selectedBuckles.length} buckle${MAX_BUCKLES - filterManagers.buckle.selectedBuckles.length !== 1 ? 's' : ''}`);
      hint.textContent = 'Still need: ' + need.join(' and ');
      hint.style.color = '#201e1eff';
    }
  }

  /* Tooltip: set up listeners once; auto-hide as soon as buttons become enabled */
  _bsbInitTooltip();
  if (ready) _bsbHideTooltip();

  updateAddonSystem();
}

/* ══════════════════════════════════════════════════════════
   7b. ATC TOOLTIP — shows what still needs to be selected
       on hover (desktop) or tap (mobile) of any disabled
       Add to Cart button.
══════════════════════════════════════════════════════════ */
var _bsbTip       = null;   /* singleton <div.bsb-atc-tooltip> */
var _bsbTipInited = false;  /* init guard — run only once      */

function _bsbInitTooltip() {
  if (_bsbTipInited) return;
  _bsbTipInited = true;

  /* Create the shared tooltip element and append to body */
  _bsbTip = document.createElement('div');
  _bsbTip.className = 'bsb-atc-tooltip';
  _bsbTip.setAttribute('role', 'tooltip');
  _bsbTip.setAttribute('aria-hidden', 'true');
  document.body.appendChild(_bsbTip);

  var ATC_IDS = ['sidebarAtcBtn', 'stickyAtcBtn', 'mobileAtcBtn', 'finalAtcBtn'];

  ATC_IDS.forEach(function(id) {
    var btn = document.getElementById(id);
    if (!btn) return;

    /* Desktop — show on hover, hide on leave */
    btn.addEventListener('mouseenter', function() {
      if (btn.disabled) _bsbShowTooltip(btn);
    });
    btn.addEventListener('mouseleave', _bsbHideTooltip);

    /* Mobile — tap on disabled button toggles tooltip.
       touchend fires on disabled elements unlike click. */
    btn.addEventListener('touchend', function(e) {
      if (!btn.disabled) return;
      e.preventDefault(); /* suppress ghost click */
      if (_bsbTip._target === btn && _bsbTip.classList.contains('is-visible')) {
        _bsbHideTooltip();
      } else {
        _bsbShowTooltip(btn);
      }
    }, { passive: false });
  });

  /* Dismiss tooltip when tapping elsewhere on mobile */
  document.addEventListener('touchstart', function(e) {
    if (!_bsbTip || !_bsbTip.classList.contains('is-visible')) return;
    var onBtn = ATC_IDS.some(function(id) {
      var b = document.getElementById(id);
      return b && b.contains(e.target);
    });
    if (!onBtn) _bsbHideTooltip();
  }, { passive: true });
}

/** Build the "PLEASE SELECT X STRAPS AND Y BUCKLES" message */
function _bsbGetTooltipMsg() {
  var sl = Math.max(0, MAX_STRAPS  - filterManagers.strap.selectedStraps.length);
  var bl = Math.max(0, MAX_BUCKLES - filterManagers.buckle.selectedBuckles.length);
  var parts = [];
if (sl > 0) {
  if (sl === 1) {
    parts.push('1 MORE STRAP');
  } else if (sl === 2) {
    parts.push('2 MORE STRAPS');
  } else {
    parts.push(sl + ' STRAPS');
  }
}
  if (bl > 0) parts.push(bl + (bl === 1 ? ' MORE BUCKLE' : ' BUCKLES'));
  if (!parts.length) return '';
  return 'PLEASE SELECT ' + parts.join(' AND ');
}

/** Position and reveal the tooltip above btn */
function _bsbShowTooltip(btn) {
  if (!_bsbTip) return;
  var msg = _bsbGetTooltipMsg();
  if (!msg) return;
  _bsbTip.textContent = msg;
  _bsbTip._target = btn;
  /* Move off-screen so we can measure size before the fade-in lands it */
  _bsbTip.style.left = '-9999px';
  _bsbTip.style.top  = '-9999px';
  _bsbTip.classList.add('is-visible');
  /* Reposition once the browser has laid out the tooltip content */
  requestAnimationFrame(function() {
    var r    = btn.getBoundingClientRect();
    var tw   = _bsbTip.offsetWidth;
    var th   = _bsbTip.offsetHeight;
    var left = r.left + r.width / 2 - tw / 2;
    /* Clamp so tooltip never bleeds off the viewport edges */
    left = Math.max(8, Math.min(left, window.innerWidth - tw - 8));
    var top = r.top - th - 12; /* 12 px gap above the button */
    _bsbTip.style.left = left + 'px';
    _bsbTip.style.top  = top  + 'px';
  });
}

/** Hide the tooltip (no-op if already hidden) */
function _bsbHideTooltip() {
  if (!_bsbTip) return;
  _bsbTip.classList.remove('is-visible');
  _bsbTip._target = null;
}

/** Number of selected straps flagged as add-ons via metafields.custom.addon */
function getAddonCount() {
  return filterManagers.strap.selectedStraps.filter(function(id) {
    var p = ALL_PRODUCTS.find(function(x){ return x.id == id; });
    return p && p.isAddon;
  }).length;
}

/** Sum of prices (in cents) of add-on straps that are selected */
function getAddonTotal() {
  return filterManagers.strap.selectedStraps.reduce(function(sum, id) {
    var p = ALL_PRODUCTS.find(function(x){ return x.id == id; });
    return (p && p.isAddon) ? sum + (p.price || 0) : sum;
  }, 0);
}

/** Sidebar subtotal price:
 *  - Upgrade accepted but 3rd buckle not yet chosen: preview the full 6-item price immediately
 *  - Full selection: mirrors actual bundle price from getCurrentPrice()
 *  - Partial selection: $99.99 base + addon upcharges for selected premium straps */
function getDisplayPrice() {
  /* After upgrade accepted, jump straight to the 6-item tier so the
     price reflects what they will pay the moment they click "Yes". */
  if (hasUpgraded && filterManagers.buckle.selectedBuckles.length < MAX_BUCKLES) {
    var addonCount   = getAddonCount();
    var upgradeKey   = (MAX_STRAPS + MAX_BUCKLES) + '_' + addonCount;
    var upgradeVid   = VARIANT_MAP[upgradeKey] ? String(VARIANT_MAP[upgradeKey]) : null;
    var upgradePrice = upgradeVid && SHOPIFY_VARIANTS[upgradeVid] && SHOPIFY_VARIANTS[upgradeVid].price != null
                       ? SHOPIFY_VARIANTS[upgradeVid].price
                       : (SELECTION_PRICE_MAP[upgradeKey] != null ? SELECTION_PRICE_MAP[upgradeKey] : null);
    if (upgradePrice != null) return upgradePrice;
  }

  var price   = getCurrentPrice();
  var baseVid = VARIANT_MAP['5_0'] ? String(VARIANT_MAP['5_0']) : null;
  var base99  = (baseVid && SHOPIFY_VARIANTS[baseVid] && SHOPIFY_VARIANTS[baseVid].price != null)
                ? SHOPIFY_VARIANTS[baseVid].price
                : (SELECTION_PRICE_MAP['5_0'] != null ? SELECTION_PRICE_MAP['5_0'] : BOX_PRICE);
  /* A full selection resolves to a real variant: show exactly what Shopify will charge.
     (Comparing against BOX_PRICE is not reliable when the product's cheapest variant is not the base one.)
     Partial selection: base price + the add-on prices of the selected premium straps. */
  var _hasVariant = !!VARIANT_MAP[getCurrentVariantKey()];
  return _hasVariant ? price : base99 + getAddonTotal();
}

/** Total number of selected straps + buckles */
function getTotalSelCount() {
  return filterManagers.strap.selectedStraps.length + filterManagers.buckle.selectedBuckles.length;
}

/** VARIANT_MAP lookup key: "totalItems_addonCount" */
function getCurrentVariantKey() {
  return getTotalSelCount() + '_' + getAddonCount();
}

/**
 * Resolve the sale price to display (and use for the ATC button label).
 *
 * Priority (highest → lowest):
 *  1. SHOPIFY_VARIANTS[vid].price  — real Shopify variant price (auto from product picker)
 *  2. VARIANT_PRICE_MAP[vid]       — manual override JSON in theme settings
 *  3. BOX_PRICE                    — flat fallback when no product is linked
 *
 * Add-on prices are NEVER added here. The variant key already encodes
 * the add-on count (e.g. "6_3"), so Shopify's variant price already
 * accounts for the full configuration. getAddonTotal() is only used by
 * syncAddonCart() for the separate add-on line item — never for display.
 */
function getCurrentPrice() {
  var key    = getCurrentVariantKey();
  var vid    = VARIANT_MAP[key];
  var vidStr = vid ? String(vid) : null;

  /* 1. Real Shopify variant price */
  if (vidStr && SHOPIFY_VARIANTS[vidStr] && SHOPIFY_VARIANTS[vidStr].price != null) {
    return SHOPIFY_VARIANTS[vidStr].price;
  }
  /* 2. Manual override */
  if (vidStr && VARIANT_PRICE_MAP[vidStr] != null) {
    return VARIANT_PRICE_MAP[vidStr];
  }
  /* 3. Selection-key price table (inline fallback — always reflects variant pricing) */
  if (SELECTION_PRICE_MAP[key] != null) {
    return SELECTION_PRICE_MAP[key];
  }
  /* 4. Flat fallback */
  return BOX_PRICE;
}

/**
 * Resolve the compare-at (strikethrough) price.
 *
 * Priority (highest → lowest):
 *  1. SHOPIFY_VARIANTS[vid].compare_at_price — real Shopify compare price
 *  2. VARIANT_COMPARE_PRICE_MAP[vid]         — manual override JSON
 *  3. BOX_COMPARE_PRICE                      — flat fallback
 *
 * Returns 0 when no compare price is set (hides the strikethrough element).
 */
function getCurrentComparePrice() {
  var key    = getCurrentVariantKey();
  var vid    = VARIANT_MAP[key];
  var vidStr = vid ? String(vid) : null;

  /* 1. Real Shopify compare_at_price */
  if (vidStr && SHOPIFY_VARIANTS[vidStr] && SHOPIFY_VARIANTS[vidStr].compare_at_price > 0) {
    return SHOPIFY_VARIANTS[vidStr].compare_at_price;
  }
  /* 2. Manual override */
  if (vidStr && VARIANT_COMPARE_PRICE_MAP[vidStr] != null) {
    return VARIANT_COMPARE_PRICE_MAP[vidStr];
  }
  /* 3. Flat fallback */
  return BOX_COMPARE_PRICE;
}

/**
 * Write variantId into every Shopify variant selector on the page.
 * ⚠️ We deliberately do NOT dispatch the native 'change' event here —
 *    that caused the wrong-variant-in-cart bug (Shopify's product.js
 *    would reset the input to the default variant on 'change').
 */
function syncVariantInputs(variantId) {
  if (!variantId) return;
  var vid = String(variantId);
  document.querySelectorAll('input[name="id"]').forEach(function(el)  { el.value = vid; });
  document.querySelectorAll('select[name="id"]').forEach(function(el) { el.value = vid; });
  var ps = document.getElementById('productSelect');
  if (ps) ps.value = vid;
}

function ensureValidVariant() {
  var resolved = resolveVariantId();
  if (!resolved) {
    console.error('[BSB] [ensureValidVariant] Key "' + getCurrentVariantKey() +
                  '" is not in VARIANT_MAP. Valid keys: ' + Object.keys(VARIANT_MAP).join(', '));
    return false;
  }
  return resolved.vid;
}

/* ══════════════════════════════════════════════════════════
   8. VARIANT RESOLUTION
══════════════════════════════════════════════════════════ */
function resolveVariantId() {
  var total    = getTotalSelCount();
  var addons   = getAddonCount();
  var exactKey = total + '_' + addons;
  var mapped   = VARIANT_MAP[exactKey];

  if (mapped) {
    _bsbLockedVid = mapped;
    _bsbLockedKey = exactKey;
    return { vid: mapped, key: exactKey, isFallback: false };
  }

  _bsbLockedVid = 0;
  _bsbLockedKey = '';
  return null;
}

function updateVariantBySelectionCount() {
  var resolved = resolveVariantId();
  var vid      = resolved ? resolved.vid : null;
  var price    = getCurrentPrice();

  if (vid) syncVariantInputs(vid);

  var vidStr      = vid ? String(vid) : null;
  var priceSource = vidStr && SHOPIFY_VARIANTS[vidStr] && SHOPIFY_VARIANTS[vidStr].price != null
                    ? 'shopify_variant'
                    : vidStr && VARIANT_PRICE_MAP[vidStr] != null
                      ? 'variant_price_map'
                      : 'box_price_fallback';

  console.log('[BSB STATE]', {
    straps:      filterManagers.strap.selectedStraps.slice(),
    buckles:     filterManagers.buckle.selectedBuckles.slice(),
    addons:      getAddonCount(),
    total:       getTotalSelCount(),
    variantKey:  getCurrentVariantKey(),
    variantId:   vid || null,
    isFallback:  resolved ? resolved.isFallback : false,
    price:       price,
    priceSource: priceSource
  });
}

function DynamicPrice() {
  var price        = getCurrentPrice();
  var comparePrice = getCurrentComparePrice();
  var priceFmt     = '$' + (price / 100).toFixed(2);
  /* Only show compare-at when it is strictly greater than the sale price */
  var compareFmt   = (comparePrice > 0 && comparePrice > price)
                     ? '$' + (comparePrice / 100).toFixed(2)
                     : '';

  /* ── Generic price elements ── */
  document.querySelectorAll('.dynamicPrice, #ProductPrice').forEach(function(el) {
    el.textContent = priceFmt;
  });
  /* ── BSB price display elements (incl. #mobileSubtotal in the peek strip)
     Use getDisplayPrice() so the 6-item price shows immediately after upgrade,
     before the 3rd buckle is selected. ── */
  var displayFmt = '$' + (getDisplayPrice() / 100).toFixed(2);
  document.querySelectorAll('[data-bsb-price]').forEach(function(el) {
    el.textContent = displayFmt;
  });

  /* ── Compare-at price elements (hidden when no compare price) ── */
  document.querySelectorAll('[data-bsb-compare]').forEach(function(el) {
    if (compareFmt) {
      el.textContent = compareFmt;
      el.style.display = '';
    } else {
      el.textContent  = '';
      el.style.display = 'none';
    }
  });

  /* ── Klarna instalment amount (price ÷ 4) ── */
  var klarnaAmt = '$' + (price / 100 / 4).toFixed(2);
  document.querySelectorAll('[data-klarna-amount]').forEach(function(el) {
    el.textContent = klarnaAmt;
  });

  /* ── Sidebar subtotal price: $99.99 base + addon upcharges, matches ATC when complete ── */
  var ssp = document.getElementById('sidebarSubtotalPrice');
  if (ssp) ssp.textContent = '$' + (getDisplayPrice() / 100).toFixed(2);

  /* ── Add to Cart buttons: update the data-atc-price span if present,
        otherwise fall back to replacing the full button text ── */
  var atcFmt = '$' + (getDisplayPrice() / 100).toFixed(2);
  ['sidebarAtcBtn', 'stickyAtcBtn', 'mobileAtcBtn', 'finalAtcBtn'].forEach(function(id) {
    var btn = document.getElementById(id);
    if (!btn) return;
    var sp = btn.querySelector('[data-atc-price]');
    if (sp) {
      sp.textContent = atcFmt;
    } else {
     btn.innerHTML = '<i class="fa-solid fa-shopping-cart mr-2" aria-hidden="true"></i> Add to Cart (' + atcFmt + ') →';
    }
  });
}

function syncAddonCart() {
  if (!ADDON_CART_VID) return Promise.resolve();
  if (_cartUpdateInProgress) return Promise.resolve();
  _cartUpdateInProgress = true;
  var addonQty = getAddonCount();
  return fetch('/cart/update.js', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ updates: { [ADDON_CART_VID]: addonQty } })
  })
  .then(function(r) { return r.json(); })
  .then(function()  { _cartUpdateInProgress = false; })
  .catch(function() { _cartUpdateInProgress = false; });
}

/** Master wrapper — call after any selection change */
function updateAddonSystem() {
  updateVariantBySelectionCount();
  DynamicPrice();
}

/* ══════════════════════════════════════════════════════════
   9. ADD TO CART HANDLER
══════════════════════════════════════════════════════════ */

/* Toggle loading state on ATC buttons.
   loading=true  → shows spinner ONLY on the button that was clicked (targetBtn)
   loading=false → restores ALL buttons (cleans up whichever one was loading)  */
function _setAtcLoading(loading, targetBtn) {
  if (loading) {
    /* Only apply the spinner to the button the user actually clicked.
       If no button reference was passed, skip — never fall back to a wrong button. */
    if (!targetBtn) return;
    targetBtn.dataset.bsbOrigHtml = targetBtn.innerHTML;
    targetBtn.innerHTML = '<span class="bsb-atc-spinner"></span>✓ Adding to cart…';
    targetBtn.classList.add('is-loading');
  } else {
    /* On finish (success or error) restore every button cleanly */
    ['sidebarAtcBtn','stickyAtcBtn','mobileAtcBtn','finalAtcBtn'].forEach(function(id) {
      var btn = document.getElementById(id);
      if (!btn) return;
      if (btn.dataset.bsbOrigHtml) {
        btn.innerHTML = btn.dataset.bsbOrigHtml;
        delete btn.dataset.bsbOrigHtml;
      }
      btn.classList.remove('is-loading');
    });
  }
}

function handleAtcClick(clickedBtn) {
  if (_cartUpdateInProgress) { console.log('[BSB] ATC blocked — request in progress'); return; }

  /* ── 1. Selections complete? ── */
  var strapsOk  = filterManagers.strap.selectedStraps.length === MAX_STRAPS;
  var bucklesOk = filterManagers.buckle.selectedBuckles.length === MAX_BUCKLES;
  if (!strapsOk || !bucklesOk) {
    showToast('Please complete your selections first.', 'warning', '✅');
    return;
  }

  /* ── Show packaging modal — user must choose Box or Travel Bags ── */
  _cartUpdateInProgress = false;
  _pendingAtcBtn = clickedBtn || null;
  var _packMode = (document.getElementById('cbPackagingModal') || {}).dataset && document.getElementById('cbPackagingModal').dataset.packMode;
  if (_packMode === 'no_popup') {
  _executeAtcWithPackaging('Anson Travel Bags');
  } else {
    showPackagingModal();
  }
}

/* ── Build cart line-item properties (shared by both packaging paths) ── */
function _buildAtcProperties() {
  var properties = {};

  /* Position 1 — Width (decimal → hyphen, e.g. 1.25 → 1-25) */
  if (selectedSize) {
    properties['Width'] = String(selectedSize).replace('.', '-');
  }

  /* Position 2+ — Straps */
  filterManagers.strap.selectedStraps.forEach(function(id, i) {
    var p    = ALL_PRODUCTS.find(function(x){ return x.id == id; });
    var name = p ? _cleanTitle(p.title) : String(id);
    var sku  = p && p.sku ? p.sku : '';
    properties['Strap ' + (i + 1)] = sku ? name + ' SKU: ' + sku : name;
  });

  /* Which straps are premium (their numbers, e.g. "1,3"). Hidden "_" key: the shopper never sees it; the cart
     uses it to put the PREMIUM badge on those straps. */
  var _premStraps = [];
  filterManagers.strap.selectedStraps.forEach(function(id, i) {
    var p = ALL_PRODUCTS.find(function(x){ return x.id == id; });
    if (p && p.isAddon) _premStraps.push(i + 1);
  });
  if (_premStraps.length) properties['_premium_straps'] = _premStraps.join(',');

  /* Buckles follow after straps */
  filterManagers.buckle.selectedBuckles.forEach(function(id, i) {
    var p    = ALL_PRODUCTS.find(function(x){ return x.id == id; });
    var name = p ? _cleanTitle(p.title) : String(id);
    var sku  = p && p.sku ? p.sku : '';
    properties['Buckle ' + (i + 1)] = sku ? name + ' SKU: ' + sku : name;
  });

  return properties;
}

/* ── Execute the actual cart add after packaging is selected ── */
function _executeAtcWithPackaging(packagingLabel) {
  if (_cartUpdateInProgress) return;
  _cartUpdateInProgress = true;

  _setAtcLoading(true, _pendingAtcBtn || null);

  /* Packaging first so it appears directly after the variant options in the cart */
  var _atcProps = _buildAtcProperties();
  var properties = {};
  if (packagingLabel) { properties['Packaging'] = packagingLabel; }
  Object.keys(_atcProps).forEach(function(k) { properties[k] = _atcProps[k]; });

  /* ── 80 ms delay — lets any lingering DOM/event updates settle ── */
  setTimeout(function() {

    /* ── Strict VARIANT_MAP lookup — the ONLY source of truth ── */
    var varKey   = getCurrentVariantKey();
    var mapEntry = VARIANT_MAP[varKey];
    var finalVid = mapEntry ? (parseInt(mapEntry, 10) || 0) : 0;

    console.log('[BSB] [ATC FETCH]', {
      variantKey: varKey,
      mapEntry:   mapEntry || 'NOT IN MAP ← add this key to VARIANT_MAP',
      finalVid:   finalVid,
      totalItems: getTotalSelCount(),
      addonCount: getAddonCount(),
      mapKeys:    Object.keys(VARIANT_MAP)
    });

    if (!finalVid) {
      _cartUpdateInProgress = false;
      _setAtcLoading(false);
      closePackagingModal();
      showToast('Variant "' + varKey + '" is not configured. Please contact support.', 'warning', '✅');
      console.error('[BSB] BLOCKED — "' + varKey + '" not found in VARIANT_MAP.',
                    'Valid keys:', Object.keys(VARIANT_MAP).join(', '),
                    '| totalItems:', getTotalSelCount(), '| addonCount:', getAddonCount());
      return;
    }

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        id:           finalVid,
        quantity:     1,
        properties:   properties,
        sections:     ['cart-drawer', 'cart-icon-bubble'],
        sections_url: '/'
      })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      _cartUpdateInProgress = false;
      _setAtcLoading(false);
      if (data.id) {
        showToast('Added to cart!', 'success', '');
        (function(secs) {
          if (!secs || !secs['cart-icon-bubble']) return;
          try {
            var tmp    = new DOMParser().parseFromString(secs['cart-icon-bubble'], 'text/html');
            var bubble = tmp.querySelector('#cart-icon-bubble');
            document.querySelectorAll('#cart-icon-bubble').forEach(function(el) {
              if (bubble) el.innerHTML = bubble.innerHTML;
            });
          } catch(e) {}
        })(data.sections);
        syncAddonCart().then(function() {
          closePackagingModal();
          openDawnCartDrawer(null);
        });
      } else {
        closePackagingModal();
        showToast(data.description || data.message || 'Could not add to cart.', 'warning', '✅');
        console.error('[BSB] /cart/add.js rejected:', data);
      }
    })
    .catch(function(err) {
      _cartUpdateInProgress = false;
      _setAtcLoading(false);
      closePackagingModal();
      showToast('Error adding to cart. Please try again.', 'warning', '✅');
      console.error('[BSB] /cart/add.js fetch error:', err);
    });

  }, 80);
}

var _PACK_ICON_ARROW = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
var _PACK_ICON_CHECK  = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 13L9 17L19 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/* ══ PACKAGING MODAL — shown between ATC click and cart submit ══ */
function showPackagingModal() {
  ['box', 'bags'].forEach(function(t) {
    var card    = document.getElementById('packCard-' + t);
    var btn     = document.getElementById('packBtn-' + t);
    var tapIcon = document.getElementById('packTapIcon-' + t);
    if (!card) return;
    card.classList.remove('is-selected');
    card.style.pointerEvents = '';
    if (btn) {
      btn.classList.remove('is-confirmed');
      if (!btn.dataset.origLabel) btn.dataset.origLabel = btn.textContent.trim();
      btn.textContent = btn.dataset.origLabel;
    }
    if (tapIcon) tapIcon.innerHTML = _PACK_ICON_ARROW;
  });
  var modal = document.getElementById('cbPackagingModal');
  if (modal) {
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }
  /* Reset single-option CTA button */
  var _singleCta = document.querySelector('.cb-pack-single-cta');
  if (_singleCta) { _singleCta.disabled = false; _singleCta.textContent = 'Continue with Anson Travel Bags'; }
}

function closePackagingModal() {
  var modal = document.getElementById('cbPackagingModal');
  if (modal) modal.style.display = 'none';
  document.body.style.overflow = '';
  _pendingAtcBtn = null;
  ['packCard-box', 'packCard-bags'].forEach(function(id) {
    var card = document.getElementById(id);
    if (card) card.style.pointerEvents = '';
  });
}

function selectPackaging(type) {
  /* Reset ALL cards first */
  ['box', 'bags'].forEach(function(t) {
    var card    = document.getElementById('packCard-' + t);
    var btn     = document.getElementById('packBtn-' + t);
    var tapIcon = document.getElementById('packTapIcon-' + t);
    if (card) { card.classList.remove('is-selected'); card.style.pointerEvents = 'none'; }
    if (btn)  { if (btn.dataset.origLabel) btn.textContent = btn.dataset.origLabel; btn.classList.remove('is-confirmed'); }
    if (tapIcon) tapIcon.innerHTML = _PACK_ICON_ARROW;
  });

  /* Highlight chosen card */
  var chosenCard    = document.getElementById('packCard-' + type);
  var chosenBtn     = document.getElementById('packBtn-' + type);
  var chosenTapIcon = document.getElementById('packTapIcon-' + type);
  if (chosenCard) chosenCard.classList.add('is-selected');
  if (chosenBtn) {
    if (!chosenBtn.dataset.origLabel) chosenBtn.dataset.origLabel = chosenBtn.textContent.trim();
    chosenBtn.textContent = '✓ Adding to Cart...';
    chosenBtn.classList.add('is-confirmed');
  }
  if (chosenTapIcon) chosenTapIcon.innerHTML = _PACK_ICON_CHECK;

  var label = type === 'box' ? 'Box Set Packaging' : 'Anson Travel Bags';

  setTimeout(function() {
    _executeAtcWithPackaging(label);
  }, 700);
}

/* ══════════════════════════════════════════════════════════
   10. DAWN SLIDE CART OPENER
   DOM structure (from snippets/cart-drawer.liquid):
     <cart-drawer class="drawer">          ← custom element; .active class opens it
       <div id="CartDrawer" class="cart-drawer">  ← plain div; injection target
         <div id="CartDrawer-Overlay">...</div>
         <div class="drawer__inner">...</div>
       </div>
     </cart-drawer>
   KEY: getElementById('CartDrawer') returns the inner DIV, NOT the custom element.
   renderContents() and classList.add('animate','active') must target <cart-drawer>.
══════════════════════════════════════════════════════════ */
function openDawnCartDrawer(cartData) {
  /* <cart-drawer> custom element — has renderContents(), open(), close() methods
     AND is the element the CSS targets (.drawer.active)                          */
  var cartDrawerCE  = document.querySelector('cart-drawer');
  /* <div id="CartDrawer"> — the inner div that receives injected section HTML    */
  var cartDrawerDiv = document.getElementById('CartDrawer');

  console.log('[BSB] openDawnCartDrawer', {
    cartDrawerCE:      cartDrawerCE  ? 'found' : 'NOT FOUND',
    cartDrawerDiv:     cartDrawerDiv ? 'found' : 'NOT FOUND',
    hasRenderContents: !!(cartDrawerCE && typeof cartDrawerCE.renderContents === 'function'),
    sectionKeys:       (cartData && cartData.sections) ? Object.keys(cartData.sections) : 'none'
  });

  if (!cartDrawerCE) {
    /* Extreme fallback: no <cart-drawer> element at all — click cart icon */
    console.warn('[BSB] <cart-drawer> not found — clicking cart toggle');
    var toggle = document.querySelector('[aria-controls="CartDrawer"]') ||
                 document.querySelector('[data-cart-toggle]')           ||
                 document.querySelector('a[href="/cart"]');
    if (toggle) { toggle.click(); console.log('[BSB] Cart toggle clicked'); }
    else console.error('[BSB] No cart toggle found on page');
    return;
  }

  /*
   * activateDrawer — adds animate + active to the <cart-drawer> element.
   * This is exactly what CartDrawer.open() does in cart-drawer.js.
   * The CSS `.drawer.active` lives on <cart-drawer>, not on #CartDrawer div.
   */
  function activateDrawer() {
    setTimeout(function() {
      cartDrawerCE.classList.remove('is-empty');   /* prevent empty-cart overlay hiding content */
      cartDrawerCE.classList.add('animate', 'active');
    }, 0);
    document.body.classList.add('overflow-hidden');
    _bsbBindOverlay();
    console.log('[BSB] activateDrawer — classList.add(animate, active) on <cart-drawer>');
  }

  function _bsbBindOverlay() {
    var ov = document.getElementById('CartDrawer-Overlay');
    if (!ov || ov._bsbOverlayBound) return;
    ov._bsbOverlayBound = true;
    ov.addEventListener('click', function() {
      if (typeof cartDrawerCE.close === 'function') cartDrawerCE.close();
      else {
        cartDrawerCE.classList.remove('active');
        document.body.classList.remove('overflow-hidden');
      }
    });
  }

  function updateIconBubble(sections) {
    if (!sections || !sections['cart-icon-bubble']) return;
    try {
      var tmp    = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      var bubble = tmp.querySelector('#cart-icon-bubble');
      document.querySelectorAll('#cart-icon-bubble').forEach(function(el) {
        if (bubble) el.innerHTML = bubble.innerHTML;
      });
    } catch (e) { /* non-fatal */ }
  }

  /*
   * tryRenderContents — calls renderContents() on the <cart-drawer> custom element.
   * renderContents() injects section HTML into #CartDrawer div AND calls this.open()
   * via its own internal setTimeout, so we let Dawn handle the open entirely.
   */
  function tryRenderContents(sections) {
    if (typeof cartDrawerCE.renderContents !== 'function') return false;
    try {
      cartDrawerCE.renderContents({ id: (cartData && cartData.id) || 0, sections: sections });
      /* renderContents calls this.open() in its own setTimeout → drawer opens via Dawn.
         Dawn only removes is-empty from the inner section element, NOT from <cart-drawer>
         itself — so we strip it here to ensure cart content (not empty state) is visible. */
      cartDrawerCE.classList.remove('is-empty');
      setTimeout(function() { updateIconBubble(sections); }, 60);
      console.log('[BSB] renderContents delegated to Dawn ✓');
      return true;
    } catch (e) {
      console.warn('[BSB] renderContents threw — falling back to manual inject:', e.message);
      return false;
    }
  }

  /*
   * manualInjectAndOpen — parse section HTML, inject into #CartDrawer div,
   * then activate <cart-drawer> via classList.
   * Used only if renderContents throws.
   */
  function manualInjectAndOpen(sections) {
    if (sections && sections['cart-drawer'] && cartDrawerDiv) {
      try {
        var doc   = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        var fresh = doc.querySelector('#CartDrawer');
        if (fresh) {
          cartDrawerDiv.innerHTML = fresh.innerHTML;
          console.log('[BSB] cart-drawer HTML injected into #CartDrawer div');
        } else {
          console.warn('[BSB] #CartDrawer not found in section HTML. Keys:', Object.keys(sections));
        }
      } catch (e) { console.error('[BSB] inject error:', e); }
    }
    /* Re-bind overlay (innerHTML replacement recreates the overlay DOM node) */
    var ov = document.getElementById('CartDrawer-Overlay');
    if (ov) ov._bsbOverlayBound = false;
    activateDrawer();
    updateIconBubble(sections);
  }

  function processAndOpen(sections) {
    if (!tryRenderContents(sections)) manualInjectAndOpen(sections);
  }

  /* ── Path A: sections came back inline with /cart/add.js response ── */
  if (cartData && cartData.sections) {
    processAndOpen(cartData.sections);
    return;
  }

  /* ── Path B: no inline sections — fetch them separately ── */
  console.log('[BSB] No inline sections from /cart/add.js — fetching separately');
  fetch('/?sections=cart-drawer,cart-icon-bubble')
    .then(function(r) { return r.json(); })
    .then(function(sections) {
      console.log('[BSB] Fetched section keys:', Object.keys(sections));
      processAndOpen(sections);
    })
    .catch(function(err) {
      console.warn('[BSB] Section fetch failed:', err, '— opening with existing content');
      activateDrawer();
    });
}

/* ══════════════════════════════════════════════════════════
   11. FILTER MANAGER BASE CLASS
══════════════════════════════════════════════════════════ */
class FilterManager {
  constructor(cfg) {
    this.productType = cfg.productType;
    this.containerId = cfg.containerId;
    this.gridId      = cfg.gridId;
    this.metaId      = cfg.metaId;
    this.chipsId     = cfg.chipsId;
    this.badgeId     = cfg.badgeId || null;  /* id of the "(N active)" badge in the filter bar header */

    this.userChecked   = new Set();
    this.userRejected  = new Set();
    this.userOwned     = new Set();
    this.autoSuggested = new Set();
    this.selectedSize  = null;
    this.filterCategories = {};
    this.productsPerPage  = window.innerWidth <= 860 ? 10 : 9;
    this.currentPage      = 1;
    this.allFilteredProds = [];
    this.showAll          = false;   /* true after first "Load More" click — persists through filter changes */
  }

  extractFilters(products) {
    /* Order matches PDF design: Type → Material → Color → Width */
    this.filterCategories = { type: new Set(), material: new Set(), color: new Set(), width: new Set() };
    products.forEach(p => p.tags.forEach(tag => {
      const cat = this.getCat(tag);
      if (cat) this.filterCategories[cat].add(tag);
    }));

    /* ── Prescribed display order for strap types ──────────────────────────────
       Normalize: strip prefix, lowercase, collapse spaces → hyphens.           */
    var _STRAP_TYPE_ORDER = [
      'casual', 'formal', 'work', 'invincibelt', 'concealed-carry', 'xl'
    ];
    function _strapTypeRank(tag) {
      var base = tag.toLowerCase().replace('strap-type-', '').replace(/\s+/g, '-').trim();
      var i = _STRAP_TYPE_ORDER.indexOf(base);
      return i === -1 ? _STRAP_TYPE_ORDER.length : i;
    }

    /* ── Prescribed display order for strap materials ───────────────────────────
       Normalize: strip prefix, lowercase, remove parentheses,
       collapse spaces → hyphens, collapse double-hyphens.                       */
    var _STRAP_MATERIAL_ORDER = [
       'full-grain-leather', 'canvas', 'micro-suede','cloth', 'nylon', 'vegan-leather','tpu-coated-webbing'
    ];
    function _strapMaterialRank(tag) {
      var base = tag.toLowerCase()
        .replace(/^(strap-material-|material_)/, '')
        .replace(/[()]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '');
      var i = _STRAP_MATERIAL_ORDER.indexOf(base);
      return i === -1 ? _STRAP_MATERIAL_ORDER.length : i;
    }

    /* ── Prescribed display order for strap colors ──────────────────────────────
       Normalize: strip prefix, lowercase, remove parentheses,
       collapse spaces → hyphens, collapse double-hyphens.
       Multicolor is intentionally last — placed at end of the order array.      */
    var _STRAP_COLOR_ORDER = [
       'black','brown','brown-red-ish','brown-orange-ish','brown(Orange-ish)','Brown(Orange Ish)','dark-brown',
      'brown-light', 'light-brown', 'gray', 'grey', 'green',
      'navy', 'navy-blue', 'purple', 'red','white','multicolor'
    ];
    function _strapColorRank(tag) {
      var base = tag.toLowerCase()
        .replace(/^(strap-color-|color_)/, '')
        .replace(/[()]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '');
      var i = _STRAP_COLOR_ORDER.indexOf(base);
      return i === -1 ? _STRAP_COLOR_ORDER.length : i;
    }

    Object.keys(this.filterCategories).forEach(k => {
      if (k === 'type') {
        this.filterCategories[k] = [...this.filterCategories[k]].sort(function(a, b) {
          var diff = _strapTypeRank(a) - _strapTypeRank(b);
          return diff !== 0 ? diff : a.localeCompare(b); /* alpha tie-break for unlisted types */
        });
      } else if (k === 'material') {
        this.filterCategories[k] = [...this.filterCategories[k]].sort(function(a, b) {
          var diff = _strapMaterialRank(a) - _strapMaterialRank(b);
          return diff !== 0 ? diff : a.localeCompare(b); /* alpha tie-break for unlisted materials */
        });
      } else if (k === 'color') {
        this.filterCategories[k] = [...this.filterCategories[k]].sort(function(a, b) {
          var diff = _strapColorRank(a) - _strapColorRank(b);
          return diff !== 0 ? diff : a.localeCompare(b); /* alpha tie-break for unlisted colors */
        });
      } else {
        this.filterCategories[k] = [...this.filterCategories[k]].sort();
      }
    });
  }

  /* Tag category detection — supports both legacy (type_) and new (strap-type-) formats */
  getCat(tag) {
    if (tag.startsWith('strap-type-'))     return 'type';
    if (tag.startsWith('strap-material-')) return 'material';
    if (tag.startsWith('strap-color-'))    return 'color';
    if (tag.startsWith('strap-width-'))    return 'width';
    //if (tag.startsWith('type_'))           return 'type';
   // if (tag.startsWith('color_'))          return 'color';
    if (tag.startsWith('material_'))       return 'material';
    return null;
  }

  /* Human-readable label — strips prefix, converts hyphens/underscores, title-cases */
  formatTagName(tag) {
    const _nameOverrides = {
      'strap-color-brown (red-ish)': 'Brown (Red-Ish)',
      'strap-color-brown (orange-ish)': 'Brown (Orange-Ish)',
    };
    if (_nameOverrides[tag.toLowerCase()]) return _nameOverrides[tag.toLowerCase()];
    return tag
      .replace(/^(strap-type-|strap-material-|strap-color-|strap-width-)/, '')
      .replace(/^(type_|color_|material_)/, '')
      .replace(/-/g, ' ').replace(/_/g, ' ')
      .split(' ').filter(Boolean)
      .map(w => w[0].toUpperCase() + w.slice(1))
      .join(' ');
  }

  /* Return the raw tag prefix used for category scoping */
  _getTagPrefix(tag) {
    const prefixes = ['strap-type-','strap-material-','strap-color-','strap-width-','type_','color_','material_'];
    return prefixes.find(p => tag.startsWith(p)) || '';
  }

  /*
   * Tag matching — two passes, most-specific first:
   *
   *   1. Exact string match (fastest path — handles the vast majority of cases).
   *
   *   2. Case-insensitive + hyphen-normalised exact match.
   *      Strips the category prefix, lowercases, and collapses hyphens → spaces so
   *      "strap-color-Dark Brown" correctly matches filter "strap-color-dark-brown",
   *      and "strap-color-Brown" correctly matches filter "strap-color-brown".
   *
   * Intentionally NOT a substring / word-contains check.
   * The old "every filter word must appear anywhere in tag value" approach caused
   * "strap-color-brown" to match "strap-color-brown (orange ish)" because the
   * word "brown" is a substring of "brown (orange ish)".  These are distinct
   * colours and must never cross-match.
   */
  _tagMatchesFilter(productTags, filterTag) {
    /* Pass 1 — exact string match */
    if (productTags.includes(filterTag)) return true;
    /* Pass 2 — case-insensitive, hyphen-normalised EQUALITY (not contains) */
    const prefix = this._getTagPrefix(filterTag);
    if (!prefix) return false;
    const filterValue = filterTag.slice(prefix.length).toLowerCase().replace(/-/g, ' ').trim();
    return productTags.some(tag => {
      const lowerTag = tag.toLowerCase();
      if (!lowerTag.startsWith(prefix)) return false;
      const tagValue = lowerTag.slice(prefix.length).replace(/-/g, ' ').trim();
      return tagValue === filterValue;
    });
  }

  /*
   * Core filter logic:
   *   Within same group  → OR  (Black OR Brown)
   *   Across groups      → AND (Color AND Material AND Type)
   */
  _productMatchesFilters(product, activeFilters) {
    if (activeFilters.length === 0) return true;
    const byGroup = {};
    activeFilters.forEach(tag => {
      const cat = this.getCat(tag);
      if (!cat) return;
      if (!byGroup[cat]) byGroup[cat] = [];
      byGroup[cat].push(tag);
    });
    return Object.values(byGroup).every(groupTags =>
      groupTags.some(tag => this._tagMatchesFilter(product.tags, tag))
    );
  }

  renderFilters() {
    const colId = this.containerId === 'filterPanel' ? 'filterColumnsStraps' : 'filterColumnsBuckles';
    const container = document.getElementById(colId);
    if (!container) return;
    const headings = { type: 'Type', material: 'Material', color: 'Color', width: 'Width' };
    container.innerHTML = Object.entries(this.filterCategories).map(([cat, tags]) => {
      if (!tags.length) return '';
      const label   = headings[cat] || (cat[0].toUpperCase() + cat.slice(1) + 's');
      const visible = tags.slice(0, 5), hidden = tags.slice(5);
      return `
        <div class="filter-col" data-category="${cat}">
          <p class="filter-col__heading">${label}</p>
          <div class="filter-col__items">
            ${visible.map(t => this._filterItem(t)).join('')}
            ${hidden.length ? `
              ${hidden.map(t => `<div class="filter-item" style="display:none;" data-hidden="true">${this._filterItemContent(t)}</div>`).join('')}
              <button class="filter-more" onclick="filterManagers['${this.productType}'].toggleShowMore(this,'${cat}')">+ ${hidden.length} more ${hidden.length === 1 ? cat : cat + 's'}</button>
            ` : ''}
          </div>
        </div>`;
    }).join('');
  }

  _filterItem(tag) { return `<div class="filter-item">${this._filterItemContent(tag)}</div>`; }
  _filterItemContent(tag) {
    const cat = this.getCat(tag);
    const cm  = this.getColorMap();
    const cc  = cat === 'color' ? cm[tag] || ` ${this.formatTagName(tag)}` : null;
    return `
      <label class="filter-item__left">
        <input type="checkbox" data-tag="${tag}" onchange="filterManagers['${this.productType}'].onFilterChange(this)">
        ${cc ? `<span class="color-dot ${tag}" style="background:${cc};"></span>` : ''}
        <span class="filter-item__name">${this.formatTagName(tag)}</span>
        <span class="filter-item__count" data-cnt="${tag}"></span>
      </label>`;
  }

  toggleShowMore(btn, cat) {
    const items = btn.closest('.filter-col').querySelectorAll('.filter-item[data-hidden="true"]');
    const expanded = btn.textContent.includes('less');
    items.forEach(el => el.style.display = expanded ? 'none' : 'block');
    btn.textContent = expanded ? `+ ${items.length} more ${items.length === 1 ? cat : cat + 's'}` : 'Show less';
  }

  onFilterChange(cb) {
    const tag = cb.dataset.tag, cat = this.getCat(tag);
    if (cb.checked) {
      this.userChecked.add(tag); this.userRejected.delete(tag);
      this.autoSuggested.delete(tag); this.userOwned.add(cat);
    } else {
      this.userChecked.delete(tag); this.autoSuggested.delete(tag); this.userRejected.add(tag);
      if (![...this.userChecked].some(t => this.getCat(t) === cat)) this.userOwned.delete(cat);
    }
    this.recompute(); this.updateChips();
  }

  recompute() {
    if (!this.selectedSize) return;
    const sizeProds = this.getBySize(this.selectedSize);
    const active    = [...this.userChecked];
    const matched   = sizeProds.filter(p => this._productMatchesFilters(p, active));

    const present = new Set();
    matched.forEach(p => p.tags.forEach(t => { if (this.getCat(t)) present.add(t); }));

    this.autoSuggested.clear();
    if (active.length > 0) {
      present.forEach(tag => {
        const cat = this.getCat(tag);
        if (!this.userOwned.has(cat) && !this.userChecked.has(tag) && !this.userRejected.has(tag))
          this.autoSuggested.add(tag);
      });
    }
    this.syncCheckboxes(sizeProds, active);
    this.renderProducts(matched);
    this.updateCounts(sizeProds);
  }

  syncCheckboxes(sizeProds, active) {
    document.querySelectorAll(`#${this.containerId} input[type="checkbox"]`).forEach(cb => {
      const tag  = cb.dataset.tag;
      const isU  = this.userChecked.has(tag);
      const item = cb.closest('.filter-item');
      cb.checked = isU;
      cb.dataset.auto = 'false';
      if (!item) return;
      item.style.opacity = '1';
      item.style.pointerEvents = '';
    });
  }

  updateCounts(sizeProds) {
    const active = [...this.userChecked];
    document.querySelectorAll(`#${this.containerId} [data-cnt]`).forEach(el => {
      const tag = el.dataset.cnt;
      const cat = this.getCat(tag);
      /* Count products matching all OTHER active filters (cross-group AND) + this tag.
         Same-category active filters are excluded so within-group counts stay meaningful
         (e.g. selecting "Casual" still shows correct counts for other Type options).
         This makes the filter panel fully interdependent — transcript requirement. */
      const otherActive = cat ? active.filter(t => this.getCat(t) !== cat) : active;
      const cnt = sizeProds.filter(p =>
        this._productMatchesFilters(p, otherActive) && this._tagMatchesFilter(p.tags, tag)
      ).length;
      el.textContent = cnt > 0 ? `(${cnt})` : '';

      /* ── Gray out + block unavailable filter options ──────────────────────
         syncCheckboxes() runs before this and resets all items to opacity:1 /
         pointerEvents:''. We override here as the last write wins.
         Exemption: already-checked items stay interactive so the user can
         always uncheck a filter they previously chose.                      */
      const item = el.closest('.filter-item');
      if (item) {
        const cb      = item.querySelector('input[type="checkbox"]');
        const noMatch = cnt === 0 && !(cb && cb.checked);
        item.style.opacity       = noMatch ? '0.35' : '';
        item.style.pointerEvents = noMatch ? 'none'  : '';
        item.style.cursor        = noMatch ? 'default' : '';
      }
    });
  }

  renderProducts(products, _opts) {
    const grid = document.getElementById(this.gridId);
    const meta = document.getElementById(this.metaId);
    if (!grid || !meta) return;

    const _silent = _opts && _opts.silent;

    /* ── Fast path: same product set already displayed ──────────────────────
       When the identical array of products is re-requested (e.g. re-unlocking
       the buckle step after a strap removal/re-add), skip the loader, skip
       the hide-all/show-matching cycle, and just refresh the meta text.
       This eliminates all visual flickering on add/remove interactions.     */
    const _prevIds = this.allFilteredProds.map(function(p) { return p.id; });
    const _newIds  = products.map(function(p) { return p.id; });
    const _sameSet = !_silent &&
      _prevIds.length === _newIds.length &&
      _newIds.every(function(id, i) { return id === _prevIds[i]; });

    if (_sameSet && this.allFilteredProds.length > 0) {
      /* Products unchanged — just sync the meta text and return */
      const _shownNow  = this.showAll ? products.length : Math.min(products.length, this.productsPerPage);
      const _total     = this.getBySize(this.selectedSize).length;
      const _ac        = this.userChecked.size;
      var _fastY = _ac > 0 ? this.allFilteredProds.length : _total;
      meta.textContent = 'Showing ' + _shownNow + ' out of ' + _fastY + ' ' + this.productType + 's';
      return;
    }

    /* ── Full render path ────────────────────────────────────────────────── */

    /* Show loading overlay only when the product set actually changes */
    const _wrap   = grid.closest('.products-grid-wrap');
    const _loader = _wrap ? _wrap.querySelector('.bsb-grid-loader') : null;
    if (_loader && !_silent) {
      clearTimeout(_loader._bsbHideTimer);
      _loader._bsbShowTime      = performance.now();
      _loader.style.transition  = 'none';
      _loader.style.opacity     = '1';
      _loader.style.pointerEvents = 'auto';
    }

    this.productsPerPage = window.innerWidth <= 860 ? 10 : 9;
    this.allFilteredProds = products;
    this.currentPage = 1;

    const total      = this.getBySize(this.selectedSize).length;
    const activeCount = this.userChecked.size;
    const isBuckle   = this.productType === 'buckle';

    /* Hide ALL pre-rendered cards and remove stale utility rows */
    grid.querySelectorAll('.product-card').forEach(function(c) { c.style.display = 'none'; });
    grid.querySelector('.load-more-wrap')?.remove();
    grid.querySelector('.bsb-no-match-msg')?.remove();

    if (!products.length) {
      grid.insertAdjacentHTML('beforeend',
        '<div class="bsb-no-match-msg" style="grid-column:1/-1;text-align:center;padding:32px;color:#aaa;">' +
          '<p style="margin-bottom:12px;">No ' + this.productType + 's match your filters</p>' +
          '<button onclick="filterManagers[\'' + this.productType + '\'].clearAllFilters()" ' +
            'style="background:#5F497B;color:#fff;border:none;border-radius:6px;padding:8px 20px;font-size:13px;cursor:pointer;font-weight:600;">Clear All Filters</button>' +
        '</div>');
      if (_loader && !_silent) {
        var _el = _loader;
        requestAnimationFrame(function() {
          _el.style.transition    = 'opacity 0.2s ease';
          _el.style.opacity       = '0';
          setTimeout(function() { _el.style.pointerEvents = 'none'; _el.style.transition = ''; }, 250);
        });
      }
      meta.textContent = 'Showing 0 ' + this.productType + 's';
      return;
    }

    /* Show matching cards (respects showAll / pagination) */
    const toShow = this.showAll ? products : products.slice(0, this.productsPerPage);
    toShow.forEach(function(p) {
      var cardId = isBuckle ? 'pc-buckle-' + p.id : 'pc-' + p.id;
      var card   = document.getElementById(cardId);
      if (card) card.style.display = '';
    });

    /* Append "Show All" button when paginated */
    const shownNow = toShow.length;
    if (!this.showAll && products.length > this.productsPerPage) {
      grid.insertAdjacentHTML('beforeend', this._loadMoreHtml(shownNow, products.length));
    }

    /* Update meta text — always "Showing X out of Y" */
    var _metaY = activeCount > 0 ? products.length : total;
    meta.textContent = 'Showing ' + shownNow + ' out of ' + _metaY + ' ' + this.productType + 's';

    if (_loader && !_silent) _bsbHideLoaderWhenReady(_loader, grid);
  }

  _loadMoreHtml(shown, total) {
    return '<div class="load-more-wrap" style="grid-column:1/-1;text-align:center;padding:20px 0;">' +
      '<button class="load-more-btn" onclick="filterManagers[\'' + this.productType + '\'].loadMore()">Show All</button>' +
      '<p style="font-size:12px;color:#999;margin-top:10px;">Showing ' + shown + ' of ' + total + '</p>' +
    '</div>';
  }

  loadMore() {
    const grid = document.getElementById(this.gridId);
    if (!grid) return;
    this.showAll = true;

    grid.querySelector('.load-more-wrap')?.remove();

    /* Show remaining filtered products already in the DOM */
    const isBuckle = this.productType === 'buckle';
    this.allFilteredProds.slice(this.productsPerPage).forEach(function(p) {
      var cardId = isBuckle ? 'pc-buckle-' + p.id : 'pc-' + p.id;
      var card   = document.getElementById(cardId);
      if (card) card.style.display = '';
    });

    /* Update meta */
    const _meta = document.getElementById(this.metaId);
    if (_meta) {
      const _allShown  = this.allFilteredProds.length;
      const _sizeTotal = this.getBySize(this.selectedSize).length;
      var _lmY = this.userChecked.size > 0 ? this.allFilteredProds.length : _sizeTotal;
      _meta.textContent = 'Showing ' + _allShown + ' out of ' + _lmY + ' ' + this.productType + 's';
    }
  }

  updateChips() {
    const chips = document.getElementById(this.chipsId);
    if (!chips) return;
    const tags = [...this.userChecked];

    /* ── Header badge: "(N active)" pill next to "Filter Straps / Filter Buckles" ── */
    const badge = this.badgeId ? document.getElementById(this.badgeId) : null;
    if (badge) {
      if (tags.length > 0) {
        badge.textContent = tags.length + (tags.length === 1 ? ' active' : ' active');
        badge.style.display = '';
      } else {
        badge.style.display = 'none';
      }
    }

    /* ── Active filter pills row ── */
    chips.style.display = tags.length ? 'flex' : 'none';
    let tagsEl = chips.querySelector('.active-filters__tags');
    if (!tagsEl) {
      chips.innerHTML = '<span class="active-filters__label">Active Filters:</span><div class="active-filters__tags"></div>';
      tagsEl = chips.querySelector('.active-filters__tags');
    }

    /* Pills for each active filter + "Clear All" button at the end */
    tagsEl.innerHTML = tags.map(tag => `
      <span class="active-filter-tag" onclick="filterManagers['${this.productType}'].removeFilter('${tag}')">
        ${this.formatTagName(tag)}
        <svg viewBox="0 0 12 12" fill="none"><path d="M2 2L10 10M10 2L2 10" stroke="currentColor" stroke-linecap="round" stroke-width="2"/></svg>
      </span>`).join('')
      + (tags.length > 1
        ? `<button class="active-filter-clear-btn" onclick="filterManagers['${this.productType}'].clearAllFilters()">Clear All</button>`
        : '');
  }

  removeFilter(tag) {
    const cb = document.querySelector(`#${this.containerId} input[data-tag="${tag}"]`);
    if (cb) { cb.checked = false; this.onFilterChange(cb); }
  }

  clearAllFilters() {
    this.userChecked.clear(); this.userRejected.clear(); this.userOwned.clear(); this.autoSuggested.clear();
    document.querySelectorAll(`#${this.containerId} input[type="checkbox"]`).forEach(cb => { cb.checked = false; cb.dataset.auto = 'false'; });
    this.updateChips();
    this.renderProducts(this.getBySize(this.selectedSize));
    this.updateCounts(this.getBySize(this.selectedSize));
    /* unchecking boxes in code fires no change event, so refresh the drawer's "Show N" button here */
    if (typeof _syncFilterDrawerCta === 'function') _syncFilterDrawerCta(this.productType);
  }

  setSelectedSize(size) {
    this.selectedSize = size;
    this.showAll = false;   /* reset pagination state for fresh size selection */
    this.clearAllFilters();
    this.extractFilters(this.getBySize(size));
    this.renderFilters();
    this.renderProducts(this.getBySize(size));
    this.updateCounts(this.getBySize(size));
  }

  getBySize(size) {
    const sizeTag = 'size_' + String(size).replace('.', '-');
    return ALL_PRODUCTS.filter(p =>
      p.type === this.productType &&
      (p.tags.some(t => t.toLowerCase() === sizeTag) || p.size === size)
    );
  }
  getFiltered() {
    if (!this.selectedSize) return [];
    const sp = this.getBySize(this.selectedSize);
    const ac = [...this.userChecked];
    return sp.filter(p => this._productMatchesFilters(p, ac));
  }

  getColorMap() {
    const base = {
      'color_black':'#1a1a1a','color_brown':'#7B4A2A','color_gray':'#888','color_grey':'#888',
      'color_navy':'#2c3e8c','color_blue':'#2c3e8c','color_red':'#e74c3c','color_white':'#f0f0f0',
      'color_tan':'#d2b48c','color_green':'#27ae60','color_orange':'#e67e22','color_purple':'#8e44ad',
      'color_yellow':'#f1c40f','color_pink':'#ff69b4','color_gunmetal':'#5a5f66','color_silver':'#c0c0c0',
      'color_gold':'#d4af37','color_bronze':'#cd7f32','color_copper':'#b87333','color_brass':'#b5a642',
      'color_chrome':'#e8e8e8','color_nickel':'#8f8f8f','color_antique':'#7a6841'
    };
    const extended = {};
    Object.keys(base).forEach(k => {
      extended['strap-color-' + k.replace('color_', '')] = base[k];
    });
    const extras = {
      'strap-color-matte black':'#1a1a1a','strap-color-matte-black':'#1a1a1a',
      'strap-color-navy blue':'#2c3e8c','strap-color-navy-blue':'#2c3e8c',
      'strap-color-navy/blue/purple':'#3a2d8c','strap-color-navy-blue-purple':'#3a2d8c',
      'strap-color-brown (Light)':'#a0724a','strap-color-light-brown':'#a0724a',
      'strap-color-Dark Brown':'#4a2010','strap-color-dark-brown':'#4a2010',
      'strap-color-olive green':'#6b7c4a','strap-color-olive-green':'#6b7c4a',
      'strap-color-brown (Orange-ish)':'#ce5440','strap-color-Brown (Red-ish)':'#74484e',
    };
    return { ...base, ...extended, ...extras };
  }
}

/* ══════════════════════════════════════════════════════════
   12. STRAP FILTER (extends FilterManager)
══════════════════════════════════════════════════════════ */
class StrapFilter extends FilterManager {
  constructor() {
    super({ productType:'strap', containerId:'filterPanel', gridId:'productsGrid', metaId:'productsMeta', chipsId:'activeFiltersStraps', badgeId:'filterActiveBadgeStraps' });
    this.selectedStraps = [];
  }

  onCardClick(e, id) {
    e.stopPropagation();
    /* Safari: stepper button clicks can bubble through overflow:hidden clipping —
       if the event target is inside the stepper, delegate to the + button handler */
    if (e.target && e.target.closest && e.target.closest('.product-card__stepper')) {
      if (e.target.closest('.product-card__stepper-btn--plus') || e.target.closest('.product-card__stepper-count')) this.stepperAdd(e, id);
      else if (e.target.closest('.product-card__stepper-btn--minus')) this.stepperRemove(e, id);
      return;
    }
    const cnt = this.selectedStraps.filter(x => x === id).length;
    if (cnt === 0) {
      this.addItem(e, id);
    } else {
      this.stepperAdd(e, id);
    }
  }

  addItem(e, id) {
    if (e) e.stopPropagation();
    if (this.selectedStraps.length >= MAX_STRAPS) return;
    this.selectedStraps.push(id);
    showToast('Strap added (' + this.selectedStraps.length + '/' + MAX_STRAPS + ')', 'success', '');
    this.updateCounter(); updateSidebar(); this._patchCardStates();
    handleAutoScroll('add-strap');
  }

  stepperAdd(e, id) {
    if (e) e.stopPropagation();
    this.addItem(null, id);
  }

  stepperRemove(e, id) {
    if (e) e.stopPropagation();
    const i = this.selectedStraps.indexOf(id);
    if (i > -1) this.selectedStraps.splice(i, 1);
    showToast('Strap removed', 'remove', '✅');
    const droppedFromMax = this.selectedStraps.length === MAX_STRAPS - 1;
    this.updateCounter(); updateSidebar(); this._patchCardStates();
    if (droppedFromMax) {
      const sc2 = document.getElementById('stepCard2');
      if (sc2 && sc2.getBoundingClientRect().bottom < 50) {
        scrollToStraps(100);
      }
    }
  }

  /* Legacy — kept for any remaining references; popup no longer created */
  addAnother(e, id) { this.stepperAdd(e, id); }
  removeItem(e, id)  { this.stepperRemove(e, id); }
  closePopup(e, id)  { if (e) e.stopPropagation(); }

  /**
   * Patch card states in-place — no grid rebuild, no image reload.
   * Updates the Add button / stepper and card classes without touching <img>.
   */
  _patchCardStates() {
    const atMax = this.selectedStraps.length >= MAX_STRAPS;

    const updates = [];
    this.allFilteredProds.forEach(product => {
      const card = document.getElementById('pc-' + product.id);
      if (!card) return;
      const cnt = this.selectedStraps.filter(x => x === product.id).length;
      updates.push({ card, product, cnt, isSel: cnt > 0 });
    });

    /* Update stepper / add button immediately */
    updates.forEach(({ product, cnt, isSel }) => {
      const addBtn  = document.getElementById('add-btn-' + product.id);
      const stepper = document.getElementById('stepper-' + product.id);
      const countEl = document.getElementById('stepper-count-' + product.id);
      const plusBtn = stepper ? stepper.querySelector('.product-card__stepper-btn--plus') : null;

      if (isSel) {
        if (addBtn)  addBtn.style.display  = 'none';
        if (stepper) stepper.style.display = '';
        if (countEl) countEl.textContent   = cnt;
        if (plusBtn) plusBtn.disabled      = atMax;
      } else {
        if (addBtn)  { addBtn.style.display = ''; addBtn.disabled = atMax; }
        if (stepper) stepper.style.display  = 'none';
      }
    });

    /* Coalesce class writes into one rAF to avoid layout thrashing */
    if (this._patchRaf) cancelAnimationFrame(this._patchRaf);
    this._patchRaf = requestAnimationFrame(() => {
      this._patchRaf = null;
      updates.forEach(({ card, isSel }) => {
        card.classList.toggle('is-selected', isSel);
        card.classList.toggle('is-dimmed', atMax && !isSel);
      });
    });
  }

  updateCounter() {
    const n  = this.selectedStraps.length;
    const s2 = document.getElementById('step2Status');
    const sc = document.getElementById('strapsCount');

    if (s2) { s2.textContent = n + '/3 selected'; s2.classList.toggle('is-selected-status', n === MAX_STRAPS); }
    if (sc) { sc.textContent = n + '/3'; sc.style.color = n === MAX_STRAPS ? '#488444' : '#636466'; }
    document.getElementById('step2Indicator')?.classList.toggle('is-done', n === MAX_STRAPS);

    const remaining = MAX_STRAPS - n;
    const bucklesDone = filterManagers.buckle.selectedBuckles.length >= MAX_BUCKLES;
    if (n === MAX_STRAPS && bucklesDone) {
      updateStickyMsg('Almost done! Preview your combinations below.');
      updateStickySteps(4);
      setCurrentStep(4);
    } else if (n === MAX_STRAPS) {
      updateStickyMsg('Great! Now select your buckles (Step ' + _stepNo(3) + ')');
      updateStickySteps(3);
      setCurrentStep(3); /* straps complete — buckles is now the active step */
    } else {
    
if (n === 0) {
    updateStickyMsg(`Select ${MAX_STRAPS} strap${remaining !== 1 ? 's' : ''} (${n}/${MAX_STRAPS})`);
} else if (remaining > 0) {
    updateStickyMsg(
        `Select ${remaining} more strap${remaining !== 1 ? 's' : ''} (${n}/${MAX_STRAPS})`
    );
} else {
    updateStickyMsg(`${MAX_STRAPS} straps selected (${n}/${MAX_STRAPS})`);
}
      updateStickySteps(2);
      setCurrentStep(2);
    }

    const hint2 = document.getElementById('step2Microcopy');
    if (hint2) hint2.style.display = (n >= MAX_STRAPS) ? 'none' : '';

    if (n === MAX_STRAPS) {
      /* ── All 3 straps selected → unlock buckles step ── */
      const c3 = document.getElementById('stepCard3');
      if (c3?.classList.contains('is-locked')) {
        c3.classList.remove('is-locked'); c3.classList.add('is-open');
        document.getElementById('step3LockMessage').style.display = 'none';
        document.getElementById('step3Content').style.display = 'block';
        filterManagers.buckle.initForFirstTime();
        /* Sync step 3 status to actual buckle count — works for both first-time
           unlock (0 buckles → "0/2 selected") and re-unlock after a strap removal
           (preserved buckles → correct "N/2 selected" not a stale "0/2 selected"). */
        filterManagers.buckle.updateCounter();
      }
      /* Hide the buckles lock icon */
      const bLock = document.querySelector('#bucklesCountWrapper .icon-lock');
      if (bLock) bLock.style.display = 'none';
    } else {
      /* ── Fewer than MAX_STRAPS → keep / re-lock buckles step ── */
      const c3 = document.getElementById('stepCard3');
      if (c3 && !c3.classList.contains('is-locked')) {
        /* Preserve buckle selections AND upgrade state — only lock the UI, never wipe
           the user's picks or their upgrade decision. initialized = false so the grid
           re-renders correctly when step 3 unlocks again. */
        filterManagers.buckle.initialized = false;
        /* Re-lock step 3 card */
        c3.classList.add('is-locked');
        c3.classList.remove('is-open');
        document.getElementById('step3LockMessage').style.display = '';
        document.getElementById('step3Content').style.display = 'none';
        /* Update buckle counter UI to reflect actual preserved selections */
        const s3   = document.getElementById('step3Status');
        const cnt  = document.getElementById('bucklesCount');
        const _bn  = filterManagers.buckle.selectedBuckles.length;
        if (s3)  { s3.textContent = _bn + '/' + MAX_BUCKLES + ' selected'; s3.classList.remove('is-selected-status'); }
        if (cnt) { cnt.textContent = _bn + '/' + MAX_BUCKLES; cnt.style.color = _bn === MAX_BUCKLES ? '#488444' : '#636466'; }
        document.getElementById('step3Indicator')?.classList.remove('is-done');
        /* Lock step 4 and hide combinations / final ATC */
        _lockStep4();
        var _cs  = document.getElementById('combinationsSection'); if (_cs)  _cs.style.display  = 'none';
        var _fas = document.getElementById('finalAtcSection');      if (_fas) _fas.style.display = 'none';
        updateSidebar();
      }
      /* Restore lock icon in sidebar summary */
      const bLock = document.querySelector('#bucklesCountWrapper .icon-lock');
      if (bLock) bLock.style.display = '';
    }

    if (n === MAX_STRAPS) checkForUpgradeOffer();
    if (upgradeOffered && !upgradeModalOpen()) regenerateCombos();
    checkAllDone();
  }

  /* Card subtitle: colour · material — supports strap-* and legacy tags */
  tagsToLabel(tags) {
    const mat = (tags.find(t => t.startsWith('strap-material-'))?.replace('strap-material-','').replace(/-/g,' ')
              || tags.find(t => t.startsWith('material_'))?.replace('material_','').replace(/_/g,' ')
              || '').trim();
    const col = (tags.find(t => t.startsWith('strap-color-'))?.replace('strap-color-','').replace(/-/g,' ')
              || tags.find(t => t.startsWith('color_'))?.replace('color_','').replace(/_/g,' ')
              || '').trim();
    return [col, mat].filter(Boolean).map(s => s[0].toUpperCase() + s.slice(1)).join(' · ');
  }
}

/* ══════════════════════════════════════════════════════════
   13. BUCKLE FILTER (extends FilterManager)
══════════════════════════════════════════════════════════ */
class BuckleFilter extends FilterManager {
  constructor() {
    super({ productType:'buckle', containerId:'filterPanelBuckles', gridId:'productsGridBuckles', metaId:'productsMetaBuckles', chipsId:'activeFiltersBuckles', badgeId:'filterActiveBadgeBuckles' });
    this.selectedBuckles = [];
    this.initialized = false;
  }

  initForFirstTime() {
    if (this.initialized) return;
    const newSize = filterManagers.strap.selectedSize;

    /* Re-unlock after a strap removal: the buckle grid is already rendered for
       this size. Skip renderProducts() entirely to avoid the 350ms loader flash.
       Just patch selection state and refresh counts. */
    if (this.selectedSize === newSize && this.allFilteredProds && this.allFilteredProds.length > 0) {
      this.initialized = true;
      this.updateCounts(this.getBySize(this.selectedSize));
      this._patchCardStates();
      _initGridFades();
      return;
    }

    /* First unlock or size changed — full render */
    this.selectedSize = newSize;
    this.extractFilters(this.getBySize(this.selectedSize));
    this.renderFilters();
    this.renderProducts(this.getBySize(this.selectedSize));
    this.updateCounts(this.getBySize(this.selectedSize));
    this.initialized = true;
    _initGridFades();
  }

  /* ── Buckle-specific overrides (isolated from StrapFilter) ── */

  getCat(tag) {
    if (tag.startsWith('buckle-style-'))    return 'style';
    if (tag.startsWith('buckle-type-'))     return 'type';
    if (tag.startsWith('buckle-material-')) return 'material';
    if (tag.startsWith('buckle-color-'))    return 'color';
    //if (tag.startsWith('type_'))            return 'type';
    //if (tag.startsWith('color_'))           return 'color';
    //if (tag.startsWith('material_'))        return 'material';
    return null;
  }

  formatTagName(tag) {
    return tag
      .replace(/^(buckle-style-|buckle-type-|buckle-material-|buckle-color-)/, '')
      .replace(/^(type_|color_|material_)/, '')
      .replace(/-/g, ' ').replace(/_/g, ' ')
      .split(' ').filter(Boolean)
      .map(w => w[0].toUpperCase() + w.slice(1))
      .join(' ');
  }

  _getTagPrefix(tag) {
    const prefixes = ['buckle-style-','buckle-type-','buckle-material-','buckle-color-','type_','color_','material_'];
    return prefixes.find(p => tag.startsWith(p)) || '';
  }

  /* Categories ordered: Styles → Types → Materials → Colors */
  extractFilters(products) {
    this.filterCategories = { style: new Set(), type: new Set(), material: new Set(), color: new Set() };
    products.forEach(p => p.tags.forEach(tag => {
      const cat = this.getCat(tag);
      if (cat) this.filterCategories[cat].add(tag);
    }));
    /* Prescribed display order for buckle types.
       Normalize: strip prefix, lowercase, collapse spaces → hyphens. */
    var _BUCKLE_TYPE_ORDER = [
      'traditional', 'classic', 'compact-classic', 'golf', 'onyx', 'usa-flag'
    ];
    function _buckleTypeRank(tag) {
      var base = tag.toLowerCase().replace('buckle-type-', '').replace(/\s+/g, '-').trim();
      var i = _BUCKLE_TYPE_ORDER.indexOf(base);
      return i === -1 ? _BUCKLE_TYPE_ORDER.length : i;
    }
    /* Prescribed display order for buckle colors.
       Normalize: strip prefix, lowercase, remove parens, spaces → hyphens. */
    var _BUCKLE_COLOR_ORDER = [
       'black','gunmetal','formal-gunmetal','silver', 'gold', 'matte-gold','rose-gold', 'antique-gold','antique','matte-black',
      'nickel', 'chrome', 'satin-silver', 'dark-silver', 'antique-silver','polish-gold', 'brass', 'bronze', 'copper','onyx', 'smoke'
    ];
    function _buckleColorRank(tag) {
      var base = tag.toLowerCase()
        .replace(/^buckle-color-/, '')
        .replace(/[()]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '');
      var i = _BUCKLE_COLOR_ORDER.indexOf(base);
      return i === -1 ? _BUCKLE_COLOR_ORDER.length : i;
    }
    Object.keys(this.filterCategories).forEach(k => {
      if (k === 'type') {
        this.filterCategories[k] = [...this.filterCategories[k]].sort(function(a, b) {
          var diff = _buckleTypeRank(a) - _buckleTypeRank(b);
          return diff !== 0 ? diff : a.localeCompare(b); /* alpha tie-break for unlisted types */
        });
      } else if (k === 'color') {
        this.filterCategories[k] = [...this.filterCategories[k]].sort(function(a, b) {
          var diff = _buckleColorRank(a) - _buckleColorRank(b);
          return diff !== 0 ? diff : a.localeCompare(b); /* alpha tie-break for unlisted colors */
        });
      } else {
        this.filterCategories[k] = [...this.filterCategories[k]].sort();
      }
    });
  }

  /* Column headings match PDF design: STYLES | COLORS */
  renderFilters() {
    const container = document.getElementById('filterColumnsBuckles');
    if (!container) return;
    const headings = { style: 'Style', type: 'Type', material: 'Material', color: 'Color' };
    container.innerHTML = Object.entries(this.filterCategories).map(([cat, tags]) => {
      if (!tags.length) return '';
      const label   = headings[cat] || (cat[0].toUpperCase() + cat.slice(1) + 's');
      const visible = tags.slice(0, 5), hidden = tags.slice(5);
      return `
        <div class="filter-col" data-category="${cat}">
          <p class="filter-col__heading">${label}</p>
          <div class="filter-col__items">
            ${visible.map(t => this._filterItem(t)).join('')}
            ${hidden.length ? `
              ${hidden.map(t => `<div class="filter-item" style="display:none;" data-hidden="true">${this._filterItemContent(t)}</div>`).join('')}
              <button class="filter-more" onclick="filterManagers['buckle'].toggleShowMore(this,'${cat}')">+ ${hidden.length} more ${hidden.length === 1 ? cat : cat + 's'}</button>
            ` : ''}
          </div>
        </div>`;
    }).join('');
  }

  getColorMap() {
    const base = super.getColorMap();
    return {
      ...base,
      'buckle-color-black':'#1a1a1a','buckle-color-gunmetal':'#5a5f66',
      'buckle-color-silver':'#c0c0c0','buckle-color-gold':'#d4af37',
      'buckle-color-nickel':'#8f8f8f','buckle-color-antique':'#7a6841',
      'buckle-color-bronze':'#cd7f32','buckle-color-copper':'#b87333',
      'buckle-color-brass':'#b5a642','buckle-color-chrome':'#e8e8e8',
      'buckle-color-matte black':'#2a2a2a','buckle-color-matte-black':'#2a2a2a',
      'buckle-color-satin silver':'#b0b0b0','buckle-color-satin-silver':'#b0b0b0',
      'buckle-color-polish gold':'#d4af37','buckle-color-polish-gold':'#d4af37',
      'buckle-color-antique silver':'#9f9f9f','buckle-color-antique-silver':'#9f9f9f',
      'buckle-color-antique gold':'#b8960c','buckle-color-antiqued Gold':'#b8960c',
      'buckle-color-rose gold':'#b76e79','buckle-color-rose-gold':'#b76e79',
      'buckle-color-dark silver':'#888','buckle-color-dark-silver':'#888',
      'buckle-color-onyx':'#1a1a1a','buckle-color-smoke':'#555','buckle-color-Rose Gold':'#F9D8CE',
      'buckle-color-Matte Gold':'#EBDFC5','buckle-color-formal Gunmetal': '#7A725E',
    };
  }

  onCardClick(e, id) {
    e.stopPropagation();
    /* Safari: same bubbling guard as StrapFilter */
    if (e.target && e.target.closest && e.target.closest('.product-card__stepper')) {
      if (e.target.closest('.product-card__stepper-btn--plus') || e.target.closest('.product-card__stepper-count')) this.stepperAdd(e, id);
      else if (e.target.closest('.product-card__stepper-btn--minus')) this.stepperRemove(e, id);
      return;
    }
    const cnt = this.selectedBuckles.filter(x => x === id).length;
    if (cnt === 0) {
      this.addItem(e, id);
    } else {
      this.stepperAdd(e, id);
    }
  }

  addItem(e, id) {
    if (e) e.stopPropagation();
    if (this.selectedBuckles.length >= MAX_BUCKLES) return;
    const _wasCompleted = _combinationsShownBefore;
    this.selectedBuckles.push(id);
    showToast('Buckle added (' + this.selectedBuckles.length + '/' + MAX_BUCKLES + ')', 'success', '');
    this.updateCounter(); updateSidebar(); this._patchCardStates();
    handleAutoScroll('add-buckle', _wasCompleted);
  }

  stepperAdd(e, id) {
    if (e) e.stopPropagation();
    this.addItem(null, id);
  }

  stepperRemove(e, id) {
    if (e) e.stopPropagation();
    const i = this.selectedBuckles.indexOf(id);
    if (i > -1) this.selectedBuckles.splice(i, 1);
    showToast('Buckle removed', 'remove', '✅');
    this.updateCounter(); updateSidebar(); this._patchCardStates();
  }

  /* Legacy — kept for any remaining references; popup no longer created */
  addAnother(e, id) { this.stepperAdd(e, id); }
  removeItem(e, id)  { this.stepperRemove(e, id); }
  closePopup(e, id)  { if (e) e.stopPropagation(); }

  /**
   * Patch buckle card states in-place — no grid rebuild, no image reload.
   * Updates the Add button / stepper and card classes without touching <img>.
   */
  _patchCardStates() {
    const atMax = this.selectedBuckles.length >= MAX_BUCKLES;
    this.allFilteredProds.forEach(product => {
      const card = document.getElementById('pc-buckle-' + product.id);
      if (!card) return;
      const cnt   = this.selectedBuckles.filter(x => x === product.id).length;
      const isSel = cnt > 0;

      const addBtn  = document.getElementById('add-btn-' + product.id);
      const stepper = document.getElementById('stepper-' + product.id);
      const countEl = document.getElementById('stepper-count-' + product.id);
      const plusBtn = stepper ? stepper.querySelector('.product-card__stepper-btn--plus') : null;

      if (isSel) {
        if (addBtn)  addBtn.style.display  = 'none';
        if (stepper) stepper.style.display = '';
        if (countEl) countEl.textContent   = cnt;
        if (plusBtn) plusBtn.disabled      = atMax;
      } else {
        if (addBtn)  { addBtn.style.display = ''; addBtn.disabled = atMax; }
        if (stepper) stepper.style.display  = 'none';
      }

      card.classList.toggle('is-selected', isSel);
      card.classList.toggle('is-dimmed', atMax && !isSel);
    });
  }

  updateCounter() {
    const n   = this.selectedBuckles.length;
    const s3  = document.getElementById('step3Status');
    const cnt = document.getElementById('bucklesCount');

    /* Lock icon must never show while the buckles section is active */
    const bLock = document.querySelector('#bucklesCountWrapper .icon-lock');
    if (bLock) bLock.style.display = 'none';

    if (s3)  { s3.textContent = n + '/' + MAX_BUCKLES + ' selected'; s3.classList.toggle('is-selected-status', n === MAX_BUCKLES); }
    if (cnt) { cnt.textContent = n + '/' + MAX_BUCKLES; cnt.style.color = n === MAX_BUCKLES ? '#488444' : '#636466'; }
    document.getElementById('step3Indicator')?.classList.toggle('is-done', n === MAX_BUCKLES);

    const remaining = MAX_BUCKLES - n;
    const hint3 = document.getElementById('step3Microcopy');
    if (hint3) hint3.style.display = (n >= MAX_BUCKLES) ? 'none' : '';

    if (n === MAX_BUCKLES) {
      updateStickyMsg('Almost done! Preview your combinations below.');
      updateStickySteps(4);
      setCurrentStep(4); /* buckles complete — preview is now the active step */
      if (hasUpgraded && n === 3) { generateCombinations(9); }
    } else {
const remaining = MAX_BUCKLES - n;

if (n === 0) {
    updateStickyMsg(`Select ${MAX_BUCKLES} buckles`);
} else if (remaining > 0) {
    updateStickyMsg(
        `Select ${remaining} more buckle${remaining !== 1 ? 's' : ''} (${n}/${MAX_BUCKLES})`
    );
} else {
    updateStickyMsg(`${MAX_BUCKLES} buckles selected (${n}/${MAX_BUCKLES})`);
}
      updateStickySteps(3);
      setCurrentStep(3);
      const cs = document.getElementById('combinationsSection');
      if (cs && n < (hasUpgraded ? 3 : 2)) { cs.style.display = 'none'; _lockStep4(); }
      const _fas = document.getElementById('finalAtcSection');
      if (_fas && n < MAX_BUCKLES) _fas.style.display = 'none';
    }

    if (!hasUpgraded) checkForUpgradeOffer();
    if (upgradeOffered && !upgradeModalOpen()) regenerateCombos();
    checkAllDone();
  }

  /* Card subtitle: colour · style — supports buckle-* and legacy tags */
  tagsToLabel(tags) {
    const col = (tags.find(t => t.startsWith('buckle-color-'))?.replace('buckle-color-','').replace(/-/g,' ')
              || tags.find(t => t.startsWith('color_'))?.replace('color_','').replace(/_/g,' ')
              || '').trim();
    const sty = (tags.find(t => t.startsWith('buckle-style-'))?.replace('buckle-style-','').replace(/-/g,' ')
              || tags.find(t => t.startsWith('buckle-type-'))?.replace('buckle-type-','').replace(/-/g,' ')
              || tags.find(t => t.startsWith('type_'))?.replace('type_','').replace(/_/g,' ')
              || '').trim();
    return [col, sty].filter(Boolean).map(s => s[0].toUpperCase() + s.slice(1)).join(' · ');
  }
}

/* ── INIT ── */
const filterManagers = { strap: new StrapFilter(), buckle: new BuckleFilter() };

/* ══════════════════════════════════════════════════════════
   13b. PREMIUM STRAP INFO TOOLTIP HELPERS
   Non-breaking additions — no existing functions touched.
══════════════════════════════════════════════════════════ */

/**
 * Build feature pill HTML from strap tags.
 * Shows readable labels for type / material / color / width tags.
 */
function _strapTooltipPills(tags) {
  /* Normalise to array (Liquid may give comma-string or already an array) */
  const tagArr = Array.isArray(tags)
    ? tags
    : (typeof tags === 'string' ? tags.split(',').map(function(s){ return s.trim(); }) : []);

  const prefixes = [
    'strap-type-','strap-material-','strap-color-','strap-width-',
    'buckle-type-','buckle-material-','buckle-color-','buckle-finish-',
    'type_','color_','material_','finish_'
  ];
  const stripRe = /^(strap-type-|strap-material-|strap-color-|strap-width-|buckle-type-|buckle-material-|buckle-color-|buckle-finish-|type_|color_|material_|finish_)/;
  const seen = new Set();
  return tagArr
    .filter(function(t){ return prefixes.some(function(p){ return t.startsWith(p); }); })
    .map(function(t) {
      const label = t
        .replace(stripRe, '')
        .replace(/[-_]/g, ' ')
        .split(' ').filter(Boolean)
        .map(function(w){ return w[0].toUpperCase() + w.slice(1); })
        .join(' ');
      if (!label || seen.has(label)) return '';
      seen.add(label);
      return '<span class="strap-tooltip__tag">' + label + '</span>';
    })
    .join('');
}

/* ── Shared backdrop for strap/buckle info tooltip (created once on first use) ── */
var _bsbStrapTipBackdrop = null;
function _ensureStrapTipBackdrop() {
  if (_bsbStrapTipBackdrop) return;
  _bsbStrapTipBackdrop = document.createElement('div');
  _bsbStrapTipBackdrop.id = 'bsbStrapTipBackdrop';
  /* Click outside tooltip → close it */
  _bsbStrapTipBackdrop.addEventListener('click', function() {
    document.querySelectorAll('.strap-tooltip.is-open').forEach(function(el) {
      var m = el.id.match(/^stip-(.+)$/);
      if (m) closeStrapInfo(m[1]);
    });
  });
  document.body.appendChild(_bsbStrapTipBackdrop);
}

/**
 * Open the strap/buckle info tooltip as a centered modal overlay.
 * Closes any currently open tooltip first (one at a time).
 *
 * Safari fix: tooltip is portaled to <body> before opening so it escapes
 * any parent stacking context. On Mobile Safari, position:fixed children
 * of transformed/positioned ancestors ignore z-index and appear behind the
 * backdrop — moving the node to <body> resolves this.
 */
function openStrapInfo(id) {
  _ensureStrapTipBackdrop();
  /* Close any open tooltip cleanly before opening a new one */
  document.querySelectorAll('.strap-tooltip.is-open').forEach(function(el) {
    el.classList.remove('is-open');
    /* Restore previously portaled tooltip back to its card */
    if (el._bsbOrigParent && document.contains(el._bsbOrigParent)) {
      el._bsbOrigParent.appendChild(el);
    }
    el._bsbOrigParent = null;
  });
  const tip = document.getElementById('stip-' + id);
  if (tip) {
    /* Portal to <body> so position:fixed escapes parent stacking context on Safari */
    tip._bsbOrigParent = tip.parentNode;
    document.body.appendChild(tip);

    tip.classList.add('is-open');
    _bsbStrapTipBackdrop.classList.add('is-visible');
    _bsbLockScroll();
    tip.focus && tip.focus();
  }
}

/**
 * Close the strap/buckle info tooltip and hide the backdrop.
 * Restores the tooltip element back into its original product card.
 */
function closeStrapInfo(id) {
  const tip = document.getElementById('stip-' + id);
  if (tip) {
    tip.classList.remove('is-open');
    /* Return tooltip to its original card parent (undo the Safari portal) */
    if (tip._bsbOrigParent && document.contains(tip._bsbOrigParent)) {
      tip._bsbOrigParent.appendChild(tip);
    }
    tip._bsbOrigParent = null;
  }
  if (_bsbStrapTipBackdrop) _bsbStrapTipBackdrop.classList.remove('is-visible');
  _bsbUnlockScroll();
}

/**
 * Open the centered Premium Leather Upgrade modal for a given strap id.
 * Looks up the product in ALL_PRODUCTS to populate price and description.
 */
function openPremiumModal(id) {
  const product = ALL_PRODUCTS.find(function(p) {
    return String(p.id) === String(id);
  });

  const priceEl = document.getElementById('premiumModalPrice');
  const descEl  = document.getElementById('premiumModalDesc');

  if (priceEl) {
    if (product && product.price > 0) {
      const amount = product.price / 100 % 1 === 0
        ? Math.round(product.price / 100)
        : (product.price / 100).toFixed(2);
      priceEl.textContent = '+$' + amount + ' per strap';
    } else {
      priceEl.textContent = '+$4 per strap';
    }
  }

  if (descEl)
     {
      descEl.textContent = 
       'Our premium straps feature full-grain buffalo leather, vegetable-tanned leather, or exotic embossed finishes. These leathers develop a rich patina over time and are significantly more durable than standard materials.';
  }

  const modal = document.getElementById('premiumModal');
  if (modal) {
    modal.classList.add('is-open');
    _bsbLockScroll();
  }
}

/**
 * Close the centered Premium Leather Upgrade modal.
 */
function closePremiumModal() {
  const modal = document.getElementById('premiumModal');
  if (modal) {
    modal.classList.remove('is-open');
    _bsbUnlockScroll();
  }
}

/* ══════════════════════════════════════════════════════════
   MIX WIDTHS INFO MODAL
   Triggered by "Want to mix widths?" button in Step 1.
══════════════════════════════════════════════════════════ */
function openMixWidthsModal() {
  const modal = document.getElementById('mixWidthsModal');
  if (modal) {
    modal.classList.add('is-open');
    _bsbLockScroll();
  }
}

function closeMixWidthsModal() {
  const modal = document.getElementById('mixWidthsModal');
  if (modal) {
    modal.classList.remove('is-open');
    _bsbUnlockScroll();
  }
}

/* ── Klarna info modal ── */
function openKlarnaModal() {
  /* Sync per-payment amount from the live inline Klarna text */
  var src = document.querySelector('[data-klarna-amount]');
  if (src) {
    document.querySelectorAll('[data-klarna-popup-amount]').forEach(function(el) {
      el.textContent = src.textContent;
    });
  }
  var modal = document.getElementById('klarnaModal');
  if (modal) {
    modal.classList.add('is-open');
    _bsbLockScroll();
  }
}
function closeKlarnaModal() {
  var modal = document.getElementById('klarnaModal');
  if (modal) {
    modal.classList.remove('is-open');
    _bsbUnlockScroll();
  }
}

/* ══════════════════════════════════════════════════════════
   14. WIDTH / STEP FLOW
══════════════════════════════════════════════════════════ */

/* ── Active step border highlight ──
   Removes is-current from all step cards, then marks the one
   the user should work on next with a purple glow border.
   Called whenever the "current" step changes. */
function setCurrentStep(n) {
  [1, 2, 3, 4].forEach(function(i) {
    var card = document.getElementById('stepCard' + i);
    if (card) card.classList.remove('is-current');
  });
  var target = document.getElementById('stepCard' + n);
  if (target) target.classList.add('is-current');
}

/* ── Product grid scroll fade indicators ──
   Inserts two absolutely-positioned gradient divs (top + bottom)
   into each grid wrapper. A scroll listener then toggles
   .show-fade-top / .show-fade-bottom on the wrapper. */
function _initGridFades() {
  ['strapsGridWrap', 'bucklesGridWrap'].forEach(function(id) {
    var wrap = document.getElementById(id);
    if (!wrap) return;
    /* Inject loading overlay once — renderProducts shows it during grid rebuilds
       so users see a spinner instead of blank image slots. */
    if (!wrap.querySelector('.bsb-grid-loader')) {
      var loader = document.createElement('div');
      loader.className = 'bsb-grid-loader';
      loader.innerHTML = '<div class="bsb-grid-loader__spinner"></div>';
      wrap.insertBefore(loader, wrap.firstChild);
    }
    /* Inject scroll-fade gradients (idempotent) */
    if (!wrap.querySelector('.products-grid-fade-top')) {
      var topDiv = document.createElement('div');
      topDiv.className = 'products-grid-fade-top';
      var botDiv = document.createElement('div');
      botDiv.className = 'products-grid-fade-bottom';
      wrap.insertBefore(topDiv, wrap.firstChild);
      wrap.appendChild(botDiv);
    }
  });
  _updateGridFades(); /* run immediately after injecting */
}

/*
 * _updateGridFades — batches ALL layout reads first, then ALL writes.
 * Interleaving reads (getBoundingClientRect) with writes (classList.toggle)
 * forces the browser to recalculate layout between every read, causing
 * "layout thrashing" and scroll flickering.  Separating the two phases
 * into read-batch → write-batch eliminates the problem.
 */
function _updateGridFades() {
  var stickyBar = document.getElementById('bsbStickyBar');
  /* READ #1: offsetHeight (sticky bar) — done once, before any writes */
  var stickyH = (stickyBar && stickyBar.classList.contains('is-sticky'))
                ? (stickyBar.offsetHeight || 56) : 56;
  var vh = window.innerHeight;

  /* READ phase — collect all geometry before touching any class */
  var items = ['strapsGridWrap', 'bucklesGridWrap'].map(function(id) {
    var wrap = document.getElementById(id);
    if (!wrap) return null;
    var rect = wrap.getBoundingClientRect();   /* READ */
    return {
      wrap:       wrap,
      showTop:    rect.top    < stickyH + 8,
      showBottom: rect.bottom > vh - 8
    };
  });

  /* WRITE phase — all classList changes after all reads are done */
  items.forEach(function(item) {
    if (!item) return;
    item.wrap.classList.toggle('show-fade-top',    item.showTop);    /* WRITE */
    item.wrap.classList.toggle('show-fade-bottom', item.showBottom); /* WRITE */
  });
}

/*
 * _scheduleGridFade — rAF gate so _updateGridFades runs AT MOST ONCE per
 * animation frame regardless of how many scroll / resize events fire.
 * During _smoothScrollTo each window.scrollTo() fires a scroll event
 * synchronously; without the gate _updateGridFades would run 60×/sec
 * inside the rAF loop, causing forced reflows and visible flickering.
 */
var _gridFadeScheduled = false;
function _scheduleGridFade() {
  if (_gridFadeScheduled) return;
  _gridFadeScheduled = true;
  requestAnimationFrame(function() {
    _gridFadeScheduled = false;
    _updateGridFades();
  });
}

/* Init fades on page load (idempotent — safe to call multiple times) */
(function() {
  function _boot() {
    _initGridFades(); setCurrentStep(1); DynamicPrice();
    if (FIXED_WIDTH) applyFixedWidth(FIXED_WIDTH);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', _boot);
  else _boot();
})();

function handleWidthClick(btn) {
  const newWidth = btn.dataset.width;
  const hasSelections = filterManagers.strap.selectedStraps.length > 0
                     || filterManagers.buckle.selectedBuckles.length > 0;
  if (selectedSize && selectedSize !== newWidth && hasSelections) {
    pendingWidth = newWidth;
    document.getElementById('bsbResetModal').classList.add('is-open');
  } else {
    applyWidth(btn, newWidth);
  }
}

/* Same state change as applyWidth(), minus the button, toast and scrolling. */
function applyFixedWidth(width) {
  selectedSize = width;
  var c2 = document.getElementById('stepCard2');
  if (c2) { c2.classList.remove('is-locked'); c2.classList.add('is-open'); }
  var lock = document.getElementById('lockOverlay');
  if (lock) lock.style.display = 'none';
  var content = document.getElementById('step2Content');
  if (content) content.style.display = 'block';
  var s2 = document.getElementById('step2Status');
  if (s2 && !s2.classList.contains('is-selected-status')) s2.textContent = '0/' + MAX_STRAPS + ' selected';

  filterManagers.strap.setSelectedSize(width);
  DynamicPrice();
  updateStickyMsg('Select ' + MAX_STRAPS + ' straps (0/' + MAX_STRAPS + ')');
  updateStickySteps(2);
  updateMobileBar();
  var sheet = document.getElementById('bsbBottomSheet');
  if (sheet) sheet.classList.add('is-revealed');
  setCurrentStep(2);
  _initGridFades();
}

function applyWidth(btn, width) {
  selectedSize = width;
  document.querySelectorAll('.width-btn').forEach(b => b.classList.remove('is-selected'));
  btn.classList.add('is-selected');

  const s1 = document.getElementById('step1Status');
  if (s1) { s1.textContent = 'Selected ' + width + '"'; s1.classList.add('is-selected-status'); }
  document.getElementById('step1Indicator')?.classList.add('is-done');

  const c2 = document.getElementById('stepCard2');
  c2?.classList.remove('is-locked'); c2?.classList.add('is-open');
  document.getElementById('lockOverlay').style.display = 'none';
  document.getElementById('step2Content').style.display = 'block';
  /* Update step 2 status from "Select width first" → "0/3 selected" as soon as width is chosen */
  const _s2stat = document.getElementById('step2Status');
  if (_s2stat && !_s2stat.classList.contains('is-selected-status')) {
    _s2stat.textContent = '0/3 selected';
  }

  filterManagers.strap.setSelectedSize(width);
  document.getElementById('sidebarSubtitle').textContent = 'Width: ' + width + '"';
  DynamicPrice();

  showToast('Width ' + width + '" selected', 'info', '');
  updateStickyMsg('Now select 3 straps');
  updateStickySteps(2);
  updateMobileBar(); /* sync bottom sheet after width change */
  var _sheet = document.getElementById('bsbBottomSheet');
  if (_sheet) _sheet.classList.add('is-revealed');
  setCurrentStep(2);     /* step 2 is now the active step — highlight its border */
  _initGridFades();      /* ensure fade divs exist now that the grid is populated */

  scrollToStrapSectionMobileOnly(300);  /* mobile scroll after size click */
  scrollToStrapSectionDesktopOnly(300); /* desktop scroll after size click */
}

function confirmWidthChange() {
  document.getElementById('bsbResetModal').classList.remove('is-open');
  filterManagers.strap.selectedStraps = [];
  filterManagers.strap.updateCounter();        /* reset "X/3 selected" in step 2 header */
  filterManagers.buckle.selectedBuckles = [];
  filterManagers.buckle.updateCounter();       /* reset "X/2 selected" in step 3 header */
  filterManagers.buckle.initialized = false;
  hasUpgraded = false; upgradeOffered = false; userDeclinedUpgrade = false; MAX_BUCKLES = 2;
  document.getElementById('combinationsSection').style.display = 'none';
  document.getElementById('finalAtcSection').style.display = 'none';
  _lockStep4();
  const c3 = document.getElementById('stepCard3');
  c3?.classList.add('is-locked'); c3?.classList.remove('is-open');
  document.getElementById('step3LockMessage').style.display = '';
  document.getElementById('step3Content').style.display = 'none';
  /* Restore lock icon — buckles section is locked again after width reset */
  const bLockReset = document.querySelector('#bucklesCountWrapper .icon-lock');
  if (bLockReset) bLockReset.style.display = '';
  updateSidebar(); checkAllDone();
  setCurrentStep(2); /* after reset, step 2 is the active step again */

  const btn = document.querySelector(`.width-btn[data-width="${pendingWidth}"]`);
  if (btn) applyWidth(btn, pendingWidth);
  pendingWidth = null;
  showToast('Width reset and selections cleared', 'warning', '✅');
  handleAutoScroll('reset');
}

function cancelWidthChange() {
  document.getElementById('bsbResetModal').classList.remove('is-open');
  pendingWidth = null;
}

/* ══════════════════════════════════════════════════════════
   15. TOGGLE HELPERS
══════════════════════════════════════════════════════════ */
function toggleStep(id) {
  const c = document.getElementById(id);
  if (c?.classList.contains('is-locked')) return;
  c?.classList.toggle('is-open');
}
function _syncFilterDrawerCta(type) {
  var id   = type === 'buckle' ? 'filterDrawerCountBuckles' : 'filterDrawerCountStraps';
  var span = document.getElementById(id);
  if (!span) return;
  /* filterManagers is a top-level const, so it is NOT on window — read it directly */
  var mgr = null;
  try { mgr = filterManagers[type]; } catch (e) { mgr = window.filterManagers && window.filterManagers[type]; }
  if (mgr && typeof mgr.getFiltered === 'function') {
    /* Always use the live filtered count — unaffected by pagination */
    span.textContent = mgr.getFiltered().length;
  } else if (mgr && mgr.allFilteredProds) {
    span.textContent = mgr.allFilteredProds.length;
  } else {
    /* fallback: parse the TOTAL from meta text ("Showing 9 out of 61") → 61 */
    var metaId = type === 'buckle' ? 'productsMetaBuckles' : 'productsMeta';
    var meta   = document.getElementById(metaId);
    if (meta) {
      var m = meta.textContent.match(/out of (\d+)/) || meta.textContent.match(/Showing (\d+)/);
      if (m) span.textContent = m[1];
    }
  }
}

function toggleFilter() {
  const p       = document.getElementById('filterPanel');
  const overlay = document.getElementById('filterOverlay');
  const open    = p?.classList.toggle('is-open');
  overlay?.classList.toggle('is-open', open);
  if (open) _bsbLockScroll(); else _bsbUnlockScroll();
  if (open) {
    _syncFilterDrawerCta('strap');
    history.pushState({bsb:'filter-open', panel:'strap'}, '');
  } else {
    scrollToStraps(100);
  }
  if (open && !p._drawerListenerAttached) {
    p._drawerListenerAttached = true;
    p.addEventListener('change', function(e) {
      if (e.target.type === 'checkbox') { setTimeout(function() { _syncFilterDrawerCta('strap'); }, 50); }
    });
  }
}
function toggleFilterBuckles() {
  const p       = document.getElementById('filterPanelBuckles');
  const overlay = document.getElementById('filterOverlayBuckles');
  const open    = p?.classList.toggle('is-open');
  overlay?.classList.toggle('is-open', open);
  if (open) _bsbLockScroll(); else _bsbUnlockScroll();
  if (open) {
    _syncFilterDrawerCta('buckle');
    history.pushState({bsb:'filter-open', panel:'buckle'}, '');
  } else {
    scrollToBuckleFilter(100);
  }
  if (open && !p._drawerListenerAttached) {
    p._drawerListenerAttached = true;
    p.addEventListener('change', function(e) {
      if (e.target.type === 'checkbox') { setTimeout(function() { _syncFilterDrawerCta('buckle'); }, 50); }
    });
  }
}

/* ── Browser back button: history management for filter state ──────────────
   Opening a filter panel pushes a history entry.
   Each checkbox change pushes a filter-state entry with the current tag set.
   Back button:
     1. If a filter is open → close it (stay on page)
     2. If last history entry is a filter-state → restore those tags
     3. Otherwise → stay on builder (re-push base state to prevent nav away)
   Item picks (straps/buckles) are deliberately excluded — back never removes
   something the user added to their box set.
─────────────────────────────────────────────────────────────────────────── */
(function _bsbFilterHistory() {
  history.replaceState({bsb:'base'}, '');

  /* Patch onFilterChange so every checkbox push a history entry */
  var _origFMChange = FilterManager.prototype.onFilterChange;
  FilterManager.prototype.onFilterChange = function(cb) {
    _origFMChange.call(this, cb);
    history.pushState({bsb:'filter-state', panel:this.productType, tags:[...this.userChecked]}, '');
  };

  window.addEventListener('popstate', function(e) {
    var fp  = document.getElementById('filterPanel');
    var fpb = document.getElementById('filterPanelBuckles');

    /* 1. Close whichever filter is open */
    if (fp && fp.classList.contains('is-open')) {
      fp.classList.remove('is-open');
      document.getElementById('filterOverlay')?.classList.remove('is-open');
      _bsbUnlockScroll();
      scrollToStraps(100);
      history.pushState({bsb:'base'}, '');
      return;
    }
    if (fpb && fpb.classList.contains('is-open')) {
      fpb.classList.remove('is-open');
      document.getElementById('filterOverlayBuckles')?.classList.remove('is-open');
      _bsbUnlockScroll();
      scrollToBuckleFilter(100);
      history.pushState({bsb:'base'}, '');
      return;
    }

    /* 2. Restore a previous filter state (peel off the last filter) */
    if (e.state && e.state.bsb === 'filter-state') {
      var panel = e.state.panel;
      var tags  = e.state.tags || [];
      var mgr   = panel === 'buckle' ? filterManagers.buckle : filterManagers.strap;
      mgr.userChecked   = new Set(tags);
      mgr.userRejected.clear();
      mgr.userOwned.clear();
      mgr.autoSuggested.clear();
      tags.forEach(function(t) { var cat = mgr.getCat(t); if (cat) mgr.userOwned.add(cat); });
      document.querySelectorAll('#' + mgr.containerId + ' input[type="checkbox"]').forEach(function(cb) {
        cb.checked = mgr.userChecked.has(cb.dataset.tag);
        cb.dataset.auto = 'false';
      });
      mgr.recompute();
      mgr.updateChips();
      return;
    }

    /* 3. Prevent navigating away from the builder */
    if (!e.state || !e.state.bsb) {
      history.pushState({bsb:'base'}, '');
    }
  });
})();

/* ══════════════════════════════════════════════════════════
   16. SIDEBAR SELECTED LIST
══════════════════════════════════════════════════════════ */

/* ──────────────────────────────────────────────────────────
   MOBILE BOTTOM SHEET — toggle, state sync, items list
   All functions are mobile-only (sheet hidden on desktop).
────────────────────────────────────────────────────────── */
function toggleBottomSheet() {
  const sheet   = document.getElementById('bsbBottomSheet');
  const overlay = document.getElementById('bsbSheetOverlay');
  if (!sheet || !sheet.classList.contains('is-revealed')) return;
  const opening = !sheet.classList.contains('is-open');
  sheet.classList.toggle('is-open');
  if (overlay) overlay.classList.toggle('is-open', opening);
  _updateSheetScrollFades();
}

function _collapseBottomSheet() {
  const sheet   = document.getElementById('bsbBottomSheet');
  const overlay = document.getElementById('bsbSheetOverlay');
  if (sheet && sheet.classList.contains('is-open')) {
    sheet.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
  }
}

function _updateSheetScrollFades() {
  var list    = document.getElementById('mobileSelectedItems');
  var fadeTop = document.getElementById('sheetFadeTop');
  var fadeBot = document.getElementById('sheetFadeBottom');
  if (!list || !fadeTop || !fadeBot) return;
  var scrollTop    = list.scrollTop;
  var scrollHeight = list.scrollHeight;
  var clientHeight = list.clientHeight;
  var canScroll = scrollHeight > clientHeight;
  fadeTop.classList.toggle('is-visible', scrollTop > 5);
  fadeBot.classList.toggle('is-visible', canScroll && scrollTop < scrollHeight - clientHeight - 5);
}

function _updateMobileItems() {
  var list = document.getElementById('mobileSelectedItems');
  if (!list) return;

  var sc = {}, bc = {};
  filterManagers.strap.selectedStraps.forEach(function(id)  { sc[id] = (sc[id]||0)+1; });
  filterManagers.buckle.selectedBuckles.forEach(function(id){ bc[id] = (bc[id]||0)+1; });

  var allItems = [
    ...Object.entries(sc).map(function([id,cnt]) { return { id:id, cnt:cnt, type:'strap'  }; }),
    ...Object.entries(bc).map(function([id,cnt]) { return { id:id, cnt:cnt, type:'buckle' }; })
  ];

  if (!allItems.length) {
    list.innerHTML = '<li><p class="bsb-bottom-sheet__empty">No items selected yet</p></li>';
    _updateSheetScrollFades();
    return;
  }

  list.innerHTML = allItems.map(function(item) {
    var p = ALL_PRODUCTS.find(function(x){ return x.id == item.id; });
    if (!p) return '';
    var isAddon = !!p.isAddon;
    var thumb = p.image
      ? '<img src="' + p.image + '" alt="' + p.title + '" class="bsb-bottom-sheet__item-thumb">'
      : '<span class="bsb-bottom-sheet__item-thumb--empty"></span>';
    var addonPrice = isAddon && p.price ? ' +$' + Math.round(p.price / 100) : '';
    var badge = isAddon ? '<span class="bsb-bottom-sheet__item-badge">PREMIUM' + addonPrice + '</span>' : '';
    var label = _cleanTitle(p.title) + (item.cnt > 1 ? ' (\xd7' + item.cnt + ')' : '');
    var removeCall = item.type === 'strap'
      ? 'sidebarRemoveStrap(' + item.id + ')'
      : 'sidebarRemoveBuckle(\'' + item.id + '\')';
    return '<li class="bsb-bottom-sheet__item">'
      + thumb
      + '<span class="bsb-bottom-sheet__item-name">' + label + badge + '</span>'
      + '<button class="bsb-bottom-sheet__item-remove" onclick="' + removeCall + '" title="Remove">\xd7</button>'
      + '</li>';
  }).join('');

  _updateSheetScrollFades();
}

function updateMobileBar() {
  var strapCount  = filterManagers.strap.selectedStraps.length;
  var buckleCount = filterManagers.buckle.selectedBuckles.length;
  var maxItems    = MAX_STRAPS + MAX_BUCKLES;

  /* ── Peek strip: price — use getDisplayPrice() so the 6-item price appears
     immediately when the user accepts the upgrade, before the 3rd buckle is chosen. ── */
  var subtotal = document.getElementById('mobileSubtotal');
  if (subtotal) subtotal.textContent = '$' + (getDisplayPrice() / 100).toFixed(2);

  /* ── Peek strip: progress text ── */
  var progress = document.getElementById('mobileProgress');
  if (progress) progress.textContent = (strapCount + buckleCount) + '/' + maxItems + ' items selected';

  /* ── Green circular checkmark (right of title) when all slots filled ── */
  var isComplete = strapCount >= MAX_STRAPS && buckleCount >= MAX_BUCKLES;
  var checkEl = document.getElementById('mobileToggleCheck');
  if (checkEl) checkEl.classList.toggle('is-visible', isComplete);

  /* ── "Add to Cart" hint under the price (right side) once complete ── */
  var atcHintEl = document.getElementById('mobileToggleAtcHint');
  if (atcHintEl) atcHintEl.classList.toggle('is-visible', isComplete);

  /* ── Width display ── */
  var widthEl = document.getElementById('mobileWidthDisplay');
  if (widthEl) {
    if (selectedSize) {
      widthEl.textContent = selectedSize + '"';
      widthEl.className = 'bsb-bottom-sheet__summary-val is-complete';
    } else {
      widthEl.textContent = '—';
      widthEl.className = 'bsb-bottom-sheet__summary-val is-unset';
    }
  }

  /* ── Strap count ── */
  var strapEl = document.getElementById('mobileStrapCount');
  if (strapEl) {
    strapEl.textContent = strapCount + '/' + MAX_STRAPS;
    strapEl.className = 'bsb-bottom-sheet__summary-val ' + (strapCount === MAX_STRAPS ? 'is-complete' : 'is-incomplete');
  }

  /* ── Buckle count ── */
  var buckleEl = document.getElementById('mobileBuckleCount');
  if (buckleEl) {
    buckleEl.textContent = buckleCount + '/' + MAX_BUCKLES;
    buckleEl.className = 'bsb-bottom-sheet__summary-val ' + (buckleCount === MAX_BUCKLES ? 'is-complete' : 'is-incomplete');
  }

  /* ── Items list ── */
  _updateMobileItems();

  /* ── ATC button hint text (only when disabled) ── */
  var atcBtn = document.getElementById('mobileAtcBtn');
  if (atcBtn && atcBtn.disabled) {
    if (!selectedSize) {
      atcBtn.textContent = 'Select Width First';
    } else if (strapCount < MAX_STRAPS) {
      var ns = MAX_STRAPS - strapCount;
      atcBtn.textContent = 'Select ' + ns + (strapCount > 0 ? ' More' : '') + ' Strap' + (ns !== 1 ? 's' : '');
    } else if (buckleCount < MAX_BUCKLES) {
      var nb = MAX_BUCKLES - buckleCount;
      var pricePart = hasUpgraded ? ' · $' + (getDisplayPrice() / 100).toFixed(2) : '';
      //atcBtn.textContent = 'Select ' + nb + ' More Buckle' + (nb !== 1 ? 's' : '') + pricePart;
      atcBtn.textContent = 'Select ' + nb + (buckleCount > 0 ? ' More' : '') + ' Buckle' + (nb !== 1 ? 's' : '') + pricePart;
    }
  }
}

/* Init mobile sheet: attach scroll listener + MutationObserver */
document.addEventListener('DOMContentLoaded', function() {
  var _mList = document.getElementById('mobileSelectedItems');
  if (_mList) {
    _mList.addEventListener('scroll', _updateSheetScrollFades, { passive: true });
    new MutationObserver(_updateSheetScrollFades).observe(_mList, { childList: true, subtree: true });
  }
});

function updateSidebar() {
  const list     = document.getElementById('selectedList');
  const empty    = document.getElementById('selectedEmpty');
  const subtotal = document.getElementById('sidebarSubtotal');

  const sc = {}, bc = {};
  filterManagers.strap.selectedStraps.forEach(id   => sc[id] = (sc[id]||0)+1);
  filterManagers.buckle.selectedBuckles.forEach(id => bc[id] = (bc[id]||0)+1);

  const totalItems = filterManagers.strap.selectedStraps.length + filterManagers.buckle.selectedBuckles.length;
  empty.style.display = totalItems ? 'none' : 'block';
  if (subtotal) subtotal.style.display = totalItems ? 'flex' : 'none';

  list.innerHTML = [
    ...Object.entries(sc).map(([id, cnt]) => {
      const p = ALL_PRODUCTS.find(x => x.id == id);
      if (!p) return '';
      const isAddon    = !!p.isAddon;
      const addonCls   = isAddon ? ' is-addon' : '';
      const addonPrice = isAddon && p.price ? ' +$' + Math.round(p.price / 100) : '';
      const addonBadge = isAddon ? `<span class="sidebar__item-addon-badge">PREMIUM${addonPrice}</span>` : '';
      const thumb = p.image
        ? `<img src="${p.image}" alt="${p.title}" class="sidebar__item-thumb">`
        : `<div class="sidebar__item-thumb sidebar__item-thumb--empty"></div>`;
      const label = `${_cleanTitle(p.title)}${cnt > 1 ? ` (×${cnt})` : ''}`;
      return `
        <li class="sidebar__selected-item${addonCls}" data-addon="${isAddon ? '1' : '0'}" data-addonprice="${isAddon ? (p.price || 0) : 0}">
          ${thumb}
          <span class="sidebar__item-name">${label}${addonBadge}</span>
          <button class="sidebar__item-remove" onclick="sidebarRemoveStrap(${id})" title="Remove one">×</button>
        </li>`;
    }),
    ...Object.entries(bc).map(([id, cnt]) => {
      const p = ALL_PRODUCTS.find(x => x.id == id);
      if (!p) return '';
      const thumb = p.image
        ? `<img src="${p.image}" alt="${p.title}" class="sidebar__item-thumb">`
        : `<div class="sidebar__item-thumb sidebar__item-thumb--empty"></div>`;
      const label = `${_cleanTitle(p.title)}${cnt > 1 ? ` (×${cnt})` : ''}`;
      return `
        <li class="sidebar__selected-item" data-addon="0" data-addonprice="0">
          ${thumb}
          <span class="sidebar__item-name">${label}</span>
          <button class="sidebar__item-remove" onclick="sidebarRemoveBuckle('${id}')" title="Remove one">×</button>
        </li>`;
    })
  ].join('');

  /* Sync the mobile bottom sheet with the same data */
  updateMobileBar();
}

function sidebarRemoveStrap(id) {
  const i = filterManagers.strap.selectedStraps.indexOf(id);
  if (i > -1) filterManagers.strap.selectedStraps.splice(i, 1);
  showToast('Strap removed', 'remove', '✅');
  filterManagers.strap.updateCounter();
  updateSidebar();
  filterManagers.strap._patchCardStates();
  _collapseBottomSheet();
  /* Sidebar remove: guide user back to the straps step to pick a replacement */
  handleAutoScroll('remove-strap');
}

function sidebarRemoveBuckle(id) {
  const i = filterManagers.buckle.selectedBuckles.indexOf(id);
  if (i > -1) filterManagers.buckle.selectedBuckles.splice(i, 1);
  showToast('Buckle removed', 'remove', '✅');
  filterManagers.buckle.updateCounter();
  updateSidebar();
  filterManagers.buckle._patchCardStates();
  _collapseBottomSheet();
  /* Sidebar remove: guide user back to the buckles step to pick a replacement */
  handleAutoScroll('remove-buckle');
}

/* ══════════════════════════════════════════════════════════
   17. UPGRADE MODAL
══════════════════════════════════════════════════════════ */
function upgradeModalOpen() {
  return document.getElementById('upgradeModal')?.classList.contains('is-open');
}
function checkForUpgradeOffer() {
  if (filterManagers.strap.selectedStraps.length === MAX_STRAPS
   && filterManagers.buckle.selectedBuckles.length === MAX_BUCKLES
   && !upgradeOffered && !hasUpgraded) {
    upgradeOffered = true;
    /* Cancel any pending auto-scroll (e.g. the 500ms scroll-to-combos that was
       scheduled before this modal opened) so it doesn't fire while the modal
       is visible or immediately after the user interacts with it. */
    if (_scrollTid) { clearTimeout(_scrollTid); _scrollTid = null; }
    /* Snapshot the current scroll position. acceptUpgrade() / declineUpgrade()
       restore this exact Y instantly when the modal closes, preventing iOS Safari
       from resuming any in-flight momentum scroll and drifting the page. */
    _upgradeScrollY = window.pageYOffset || window.scrollY;
    document.getElementById('upgradeModal').classList.add('is-open');
  }
}
function acceptUpgrade() {
  /* Cancel any pending scroll timer — defensive second layer in case one
     was queued in the window between modal open and user clicking Yes. */
  if (_scrollTid) { clearTimeout(_scrollTid); _scrollTid = null; }
  hasUpgraded = true;
  MAX_BUCKLES = 3;
  document.getElementById('upgradeModal').classList.remove('is-open');
  /* Instantly restore the scroll position captured when the modal opened.
     CSS `body:has(.upgrade-modal.is-open) { overflow: hidden }` has just
     been released; on iOS Safari this can kick off momentum scroll that
     sends the page to an unexpected position (reproduces on first page load).
     Bypassing html { scroll-behavior: smooth } via scrollBehavior = 'auto'
     ensures the restore is a true snap — not a competing animation. */
  document.documentElement.style.scrollBehavior = 'auto';
  window.scrollTo(0, _upgradeScrollY);
  document.documentElement.style.scrollBehavior = '';
  filterManagers.buckle.renderProducts(filterManagers.buckle.getFiltered());
  /* renderProducts() may hit the same-set guard (products unchanged, only MAX_BUCKLES
     changed from 2→3). If so, card states are still painted with atMax=true from the
     previous _patchCardStates() call — all unselected cards are still is-dimmed and
     look unclickable. Force a state refresh so the 3rd slot opens up visually. */
  filterManagers.buckle._patchCardStates();
  showToast('Upgraded! Select your 3rd buckle for 9 combinations.', 'success', '✅');
  updateStickyMsg('Select your 3rd buckle to create 9 unique combinations!');
  /* Re-sync all UI state now that MAX_BUCKLES changed from 2 → 3.
     updateCounter re-draws the counter labels, updates the sticky bar,
     hides the finalATC/combos section until the 3rd buckle is chosen,
     and calls checkAllDone() which disables ATC buttons so the
     "PLEASE SELECT 1 BUCKLE" tooltip fires correctly on hover/tap. */
  filterManagers.buckle.updateCounter();
  updateMobileBar(); /* reflect new MAX_BUCKLES = 3 in the bottom sheet */
  /* No explicit scroll here — user stays exactly where they were
     (at the buckle section) to pick their 3rd buckle. */
}
function declineUpgrade() {
  /* Guard: only act if the upgrade modal is actually open.
     Without this, pressing Escape on ANY other popup also fires declineUpgrade(),
     which cancels pending scroll timers and jumps the page to _upgradeScrollY. */
  var _um = document.getElementById('upgradeModal');
  if (!_um || !_um.classList.contains('is-open')) return;
  /* Cancel any pending scroll timer — prevents stale timers from firing
     after the modal closes (same defensive guard as acceptUpgrade). */
  if (_scrollTid) { clearTimeout(_scrollTid); _scrollTid = null; }
  userDeclinedUpgrade = true;
  document.getElementById('upgradeModal').classList.remove('is-open');
  /* Restore scroll position instantly before generating combos so that
     generateCombinations' scrollToSection('stepCard4', 380) is the ONLY
     scroll that fires — iOS momentum from modal close cannot interfere. */
  document.documentElement.style.scrollBehavior = 'auto';
  window.scrollTo(0, _upgradeScrollY);
  document.documentElement.style.scrollBehavior = '';
  if (filterManagers.strap.selectedStraps.length === MAX_STRAPS
   && filterManagers.buckle.selectedBuckles.length === MAX_BUCKLES) {
    generateCombinations(6, 500);
  }
}
function regenerateCombos() {
  const cs  = document.getElementById('combinationsSection');
  const fas = document.getElementById('finalAtcSection');
  if (hasUpgraded && filterManagers.buckle.selectedBuckles.length === 3 && filterManagers.strap.selectedStraps.length === MAX_STRAPS) {
    generateCombinations(9);
  } else if (userDeclinedUpgrade && filterManagers.buckle.selectedBuckles.length >= 2 && filterManagers.strap.selectedStraps.length === MAX_STRAPS) {
    generateCombinations(6);
  } else {
    if (cs)  cs.style.display  = 'none';
    if (fas) fas.style.display = 'none';
    _lockStep4();
  }
}

/* ══════════════════════════════════════════════════════════
   18. COMBINATIONS & CAROUSEL
══════════════════════════════════════════════════════════ */

/* ── Step 4 accordion helpers ── */
/**
 * Unlock and reveal the Step 4 "Preview Your Belt Combinations" accordion card.
 * Called by generateCombinations() once all combos are rendered.
 */
function _unlockStep4() {
  var sc4       = document.getElementById('stepCard4');
  var sc4Cont   = document.getElementById('step4Content');
  var sc4Lock   = document.getElementById('step4LockMessage');
  var s4Status  = document.getElementById('step4Status');
  if (sc4)     { sc4.classList.remove('is-locked'); sc4.classList.add('is-open'); }
  if (sc4Cont) sc4Cont.style.display = 'block';
  if (sc4Lock) sc4Lock.style.display = 'none';
  if (s4Status) s4Status.classList.add('is-selected-status');
  document.getElementById('step4Indicator')?.classList.add('is-done');
  /* Reset carousel transform so the CSS grid layout renders correctly */
  var track = document.getElementById('combosTrack');
  if (track) { track.style.transform = ''; track.style.transition = 'none'; }
}

/**
 * Lock and hide the Step 4 accordion card.
 * Called on width reset, buckle count drop, or regenerateCombos when conditions not met.
 */
function _lockStep4() {
  var sc4       = document.getElementById('stepCard4');
  var sc4Cont   = document.getElementById('step4Content');
  var sc4Lock   = document.getElementById('step4LockMessage');
  var s4Status  = document.getElementById('step4Status');
  if (sc4)     { sc4.classList.add('is-locked'); sc4.classList.remove('is-open'); }
  if (sc4Cont) sc4Cont.style.display = 'none';
  if (sc4Lock) sc4Lock.style.display = '';
  if (s4Status) { s4Status.textContent = 'Complete Steps ' + _stepNo(2) + ' & ' + _stepNo(3) + ' first'; s4Status.classList.remove('is-selected-status'); }
  document.getElementById('step4Indicator')?.classList.remove('is-done');
  carouselIndex = 0;
}

/**
 * Build the CDN combination image URL for a strap + buckle pair.
 *
 * Format : {sizeSlug}-{cleanStrap}-and-{cleanBuckle}_1025x1034.jpg
 * Example: 1-25-black-leather-and-silver-buckle_1025x1034.jpg
 *
 * "Clean" means: remove the leading size-slug prefix (e.g. "1-25-") from both
 * the strap handle and the buckle handle before composing the filename.
 */
/**
 * _comboImageUrl — Generate the CDN combination image URL.
 *
 * Cleaning steps applied to BOTH strap and buckle handle (in order):
 *   1. Trim whitespace
 *   2. Strip leading size prefix  "1-25-" | "1-5-" (only if at position 0)
 *   3. Remove "quot" artifact     "-quot-" → "-"  (HTML &quot; leakage in handle strings)
 *   4. Collapse any double-hyphens and trim edge hyphens
 *
 * Correct example
 *   selectedSize = 1.25          →  sizeSlug = "1-25"   prefix = "1-25-"
 *   strapHandle  = "1-25-black-leather"        →  cleanStrap = "black-leather"
 *   buckleHandle = "1-25-silver-quot-buckle"   →  cleanBuck  = "silver-buckle"
 *   filename     = "1-25-black-leather-and-silver-buckle_1025x1034.jpg"
 */
function _comboImageUrl(strapHandle, buckleHandle) {
  /* Guard: need a size and both handles */
  if (!selectedSize || !strapHandle || !buckleHandle) return '';

  /* Step 0 — selectedSize → slug: "1.25" → "1-25", "1.5" → "1-5"
     /\./g (global regex) replaces every dot safely */
  const sizeSlug = String(selectedSize).trim().replace(/\./g, '-');
  if (!sizeSlug) return '';

  const prefix = sizeSlug + '-';   /* e.g. "1-25-" */

  /**
   * clean(handle)
   *   1. Trim
   *   2. Remove size prefix if at start (exact match, once)
   *   3. Strip "quot" artifact with its adjacent hyphens → normalise to single "-"
   *   4. Collapse double-hyphens; trim leading/trailing hyphens
   */
  function clean(handle) {
    var h = String(handle).trim();

    /* Step 2 — remove size prefix */
    if (h.startsWith(prefix)) h = h.slice(prefix.length);

    /* Step 3 — remove "quot" (HTML &quot; leak) wherever it appears in the slug
       Patterns: "-quot-" → "-",  "quot-" at start → "",  "-quot" at end → "" */
    h = h.replace(/-quot-/g, '-')   /* middle:  silver-quot-buckle → silver-buckle */
         .replace(/^quot-/,  '')    /* leading: quot-silver         → silver        */
         .replace(/-quot$/,  '');   /* trailing: silver-quot        → silver        */

    /* Step 4 — clean up any resulting double-hyphens or edge hyphens */
    h = h.replace(/-{2,}/g, '-').replace(/^-|-$/g, '');

    return h;
  }

  const cleanStrap = clean(strapHandle);
  const cleanBuck  = clean(buckleHandle);

  /* Safety: bail if cleaning left either part empty */
  if (!cleanStrap || !cleanBuck) return '';

  const filename = sizeSlug + '-' + cleanStrap + '-and-' + cleanBuck + '_1025x1034.jpg';
  const url      = 'https://cdn.shopify.com/s/files/1/0216/8904/files/' + filename;

  console.log('[BSB combo]'
    + ' size='   + sizeSlug
    + ' strap='  + strapHandle  + ' → ' + cleanStrap
    + ' buckle=' + buckleHandle + ' → ' + cleanBuck
    + ' file='   + filename);

  return url;
}

/**
 * _onComboImgError — onerror handler for combination card images.
 * Extracted from inline HTML to avoid fragile escaped-quote issues.
 *   • If a fallback (strap's own image) exists → swap src + mark as fallback
 *   • Otherwise → replace img with a plain placeholder div
 */
function _onComboImgError(img, fallbackSrc, idx) {
  img.onerror = null;   /* prevent infinite loop */
  if (fallbackSrc) {
    img.src = fallbackSrc;
    img.classList.add('combo-img--fallback');
  } else {
    var wrap = img.closest('.combination-card__image');
    if (wrap) wrap.innerHTML = '<div class="combo-img--placeholder">Combo ' + idx + '</div>';
  }
}

function updateFinalAtc() {
  var badge = document.getElementById('finalAtcBadge');
  if (badge) badge.textContent = totalCombos + ' Unique Combinations';

  var st = document.getElementById('finalStrapsText');
  var bt = document.getElementById('finalBucklesText');
  if (st) st.textContent = filterManagers.strap.selectedStraps.length  + ' Straps selected';
  if (bt) bt.textContent = filterManagers.buckle.selectedBuckles.length + ' Buckles selected';

  /* Swap hero image based on combination count — 6 combos = standard box, 9 combos = upgraded box */
  var img = document.getElementById('finalAtcImage');
  var ph  = document.getElementById('finalAtcPlaceholder');
  if (img) {
    img.src = totalCombos === 9
      ? 'https://cdn.shopify.com/s/files/1/0216/8904/files/sixselectionimage.png?v=1779992933'
      : 'https://cdn.shopify.com/s/files/1/0216/8904/files/box-sets_e4890703-7b1c-40f9-bb38-4227bdb83be6.jpg?v=1779477101';
    img.style.display = 'block';
    if (ph) ph.style.display = 'none';
  }
}

/* True after combinations have been shown at least once.
   Lets us pick the right scroll delay: 300ms first-time vs 100ms re-completion. */
var _combinationsShownBefore = false;

function generateCombinations(count, scrollDelay) {
  const cs = document.getElementById('combinationsSection');
  const track = document.getElementById('combosTrack');
  if (!cs || !track) return;

  const strapsNeeded  = 3;
  const bucklesNeeded = count === 9 ? 3 : 2;
  if (filterManagers.strap.selectedStraps.length !== strapsNeeded
   || filterManagers.buckle.selectedBuckles.length < bucklesNeeded) {
    cs.style.display = 'none'; _lockStep4(); return;
  }

  /* ── Build slot arrays (handles duplicate selections) ── */
  const strapSlots = [], buckleSlots = [];
  const sc = {}, bc = {};
  filterManagers.strap.selectedStraps.forEach(id   => sc[id] = (sc[id]||0)+1);
  filterManagers.buckle.selectedBuckles.forEach(id => bc[id] = (bc[id]||0)+1);
  Object.entries(sc).forEach(([id,c]) => { const p = ALL_PRODUCTS.find(x => x.id == id); if (p) for (let i=0;i<c;i++) strapSlots.push(p); });
  Object.entries(bc).forEach(([id,c]) => { const p = ALL_PRODUCTS.find(x => x.id == id); if (p) for (let i=0;i<c;i++) buckleSlots.push(p); });

  const bSlots = count === 6 ? buckleSlots.slice(0, 2) : buckleSlots;

  /* ── Generate combos and pre-compute CDN image URL for each ── */
  const combos = [];
  for (let i = 0; i < strapSlots.length; i++) {
    for (let j = 0; j < bSlots.length; j++) {
      const strap  = strapSlots[i];
      const buckle = bSlots[j];
      combos.push({
        strap,
        buckle,
        index:    combos.length + 1,
        comboImg: _comboImageUrl(strap.handle, buckle.handle)
      });
    }
  }

  totalCombos   = combos.length;
  carouselIndex = 0;

  /* ── Render cards — CDN combo image inside .combination-card__image img ── */
  track.innerHTML = combos.map(function(c) {
    const cdnSrc      = c.comboImg || '';
    const fallbackSrc = c.strap.image || '';
    const lightboxSrc = cdnSrc || fallbackSrc;

    /* Escape single quotes so fallbackSrc is safe inside an HTML attribute */
    const safeFallback = fallbackSrc.replace(/'/g, '%27');

    let imgHtml;
    if (cdnSrc) {
      /* ✅ Primary: CDN combination image rendered in .combination-card__image img
         On 404/error → _onComboImgError() swaps to strap's own image cleanly      */
      imgHtml = '<img'
        + ' src="'     + cdnSrc + '"'
        + ' alt="Combination ' + c.index + '"'
        + ' loading="lazy"'
        + ' onerror="_onComboImgError(this,\'' + safeFallback + '\',' + c.index + ')"'
        + '>';
    } else if (fallbackSrc) {
      /* CDN URL could not be built — fall straight to strap's own product image */
      imgHtml = '<img src="' + fallbackSrc + '" alt="Combination ' + c.index + '" loading="lazy" class="combo-img--fallback">';
    } else {
      /* No image at all */
      imgHtml = '<div class="combo-img--placeholder">Combo ' + c.index + '</div>';
    }

    return ''
      + '<div class="combination-card" onclick="openComboGallery(' + (c.index - 1) + ')">'
      +   '<div class="combination-card__image">' + imgHtml + '</div>'
      +   '<p class="combination-card__label">Combination ' + c.index + '</p>'
      +   '<p class="combination-card__names">' + _cleanTitle(c.strap.title) + '<br>+<br>' + _cleanTitle(c.buckle.title) + '</p>'
      + '</div>';
  }).join('');

  updateCarouselNav();
  cs.style.display = 'block';
  document.getElementById('combosTitle').textContent     = `Your ${totalCombos} Possible Belt Combinations`;
  document.getElementById('combosSubtitle').textContent  = `Each strap pairs with each buckle for ${totalCombos} unique looks`;
  updateStickySteps(4);

  /* Update step 4 header status text then unlock the accordion card */
  var _s4Stat = document.getElementById('step4Status');
  if (_s4Stat) _s4Stat.textContent = ' ';
  _unlockStep4();

  combosData = combos;

  updateFinalAtc();
  var fas = document.getElementById('finalAtcSection');
  if (fas) fas.style.display = 'block';

  /* Scroll to Step 4 card so the user sees the combinations grid.
     First-time completion:  150ms — step-card body is instant display toggle; one paint frame.
     Re-completion (re-add): 100ms — section collapsed first so less DOM reflow.
     Upsell-declined:        caller passes 500ms explicitly via scrollDelay. */
  var _sd = scrollDelay != null ? scrollDelay : (_combinationsShownBefore ? 100 : 150);
  scrollToFinalSection(_sd);
  _combinationsShownBefore = true;
}

function carouselNav(dir) {
  const visible = window.innerWidth < 560 ? 1 : window.innerWidth < 860 ? 2 : 3;
  const max = Math.max(0, totalCombos - visible);
  carouselIndex = Math.max(0, Math.min(carouselIndex + dir, max));
  const card = document.querySelector('.combination-card');
  if (!card) return;
  const w = card.offsetWidth + 20;
  document.getElementById('combosTrack').style.transform = `translateX(-${carouselIndex * w}px)`;
  updateCarouselNav();

  /* finalAtcImage is intentionally static — carousel navigation never changes it */
}
function updateCarouselNav() {
  const visible = window.innerWidth < 560 ? 1 : window.innerWidth < 860 ? 2 : 3;
  const showing = Math.min(carouselIndex + visible, totalCombos);
  document.getElementById('comboCount').textContent = `${carouselIndex+1}–${showing} of ${totalCombos}`;
  document.getElementById('comboPrev').style.opacity = carouselIndex === 0 ? '0.4' : '1';
  document.getElementById('comboNext').style.opacity = showing >= totalCombos ? '0.4' : '1';
}

/* ══════════════════════════════════════════════════════════
   19. LIGHTBOX
══════════════════════════════════════════════════════════ */
function openLightbox(src) {
  if (!src) return;
  document.getElementById('comboLightboxImg').src = src;
  document.getElementById('comboLightbox').classList.add('is-open');
}
function closeLightbox() { document.getElementById('comboLightbox').classList.remove('is-open'); }

/**
 * openComboGallery(startIdx)
 * Opens the same zoom modal used for strap/buckle images, but loaded with
 * all combination images so the user can swipe/arrow through every combo.
 * startIdx — 0-based index of the card that was clicked.
 */
function openComboGallery(startIdx) {
  if (!combosData || !combosData.length) return;

  _zoomCombos = combosData;
  _zoomTotal  = combosData.length;
  _zoomIndex  = Math.min(Math.max(0, startIdx || 0), _zoomTotal - 1);

  /* Build the image array — same fallback priority as the slide rendering below */
  _zoomImages = combosData.map(function(c) { return c.comboImg || c.strap.image || ''; });

  /* Populate the slide track — mirrors generateCombinations() img logic so
     CDN 404s fall back to the strap's own image via _onComboImgError */
  var track = document.getElementById('zoomTrack');
  track.innerHTML = combosData.map(function(c, i) {
    var cdnSrc      = c.comboImg || '';
    var fallbackSrc = c.strap.image || '';
    var src         = cdnSrc || fallbackSrc;

    if (!src) {
      return '<div class="zoom-modal__slide"><div class="combo-img--placeholder">Combo ' + c.index + '</div></div>';
    }

    /* Only add onerror when using the CDN URL and a strap fallback exists */
    var safeFallback = fallbackSrc.replace(/'/g, '%27');
    var onerrorAttr  = (cdnSrc && fallbackSrc)
      ? ' onerror="_onComboImgError(this,\'' + safeFallback + '\',' + c.index + ')"'
      : '';

    return '<div class="zoom-modal__slide">'
      + '<img src="' + src + '"'
      + ' alt="Combination ' + c.index + '"'
      + ' loading="' + (i < 2 ? 'eager' : 'lazy') + '"'
      + ' draggable="false"'
      + onerrorAttr
      + '>'
      + '</div>';
  }).join('');

  /* Dot indicators */
  var dotsEl = document.getElementById('zoomDots');
  if (_zoomTotal > 1) {
    dotsEl.style.display = 'flex';
    dotsEl.innerHTML = combosData.map(function(_, i) {
      return '<button class="zoom-modal__dot' + (i === _zoomIndex ? ' is-active' : '')
           + '" onclick="zoomGoTo(' + i + ')" aria-label="Combination ' + (i + 1) + '"></button>';
    }).join('');
  } else {
    dotsEl.style.display = 'none';
    dotsEl.innerHTML = '';
  }

  document.getElementById('zoomBox').classList.toggle('zoom-modal--single', _zoomTotal === 1);

  /* Position track on the clicked slide (no animation on open) */
  track.style.transition = 'none';
  track.style.transform  = 'translateX(-' + (_zoomIndex * 100) + '%)';

  var modal = document.getElementById('zoomModal');
  modal.classList.remove('is-closing');
  modal.classList.add('is-open');
  _bsbLockScroll();

  /* Re-enable slide transition after first paint */
  requestAnimationFrame(function() {
    requestAnimationFrame(function() {
      var t = document.getElementById('zoomTrack');
      if (t) t.style.transition = '';
    });
  });

  /* Sets counter "1 / 9" AND title "Combination 1 — …" */
  _zoomUpdateCounter();
  _zoomUpdateArrows();
  _zoomAttachInteraction(); /* wires touch / drag — replaces viewport clone to avoid duplicate listeners */

  setTimeout(function() {
    var btn = document.getElementById('zoomClose');
    if (btn) btn.focus();
  }, 80);
}

/* ══════════════════════════════════════════════════════════
   20. PRODUCT IMAGE ZOOM / GALLERY MODAL
══════════════════════════════════════════════════════════ */
var _zoomImages   = [];
var _zoomIndex    = 0;
var _zoomTotal    = 0;
var _zoomTouchX   = 0;
var _zoomTouchY   = 0;
var _zoomDragging = false;
var _zoomDragX    = 0;
var _zoomDragDX   = 0;
var _zoomJustDrag = false;
/* null = regular product gallery; array ref = combination gallery mode */
var _zoomCombos   = null;

function openZoom(productId, startIdx) {
  var prod = ALL_PRODUCTS.find(function(p){ return String(p.id) === String(productId); });
  if (!prod) return;

  _zoomImages = (prod.images && prod.images.length) ? prod.images : (prod.image ? [prod.image] : []);
  if (!_zoomImages.length) return;

  _zoomTotal = _zoomImages.length;
  _zoomIndex = Math.min(Math.max(0, startIdx || 0), _zoomTotal - 1);

  var track = document.getElementById('zoomTrack');
  track.innerHTML = _zoomImages.map(function(src, i) {
    return '<div class="zoom-modal__slide">' +
      '<img src="' + src + '"' +
      ' alt="' + _cleanTitle(prod.title || 'Product') + (i > 0 ? ' — image ' + (i+1) : '') + '"' +
      ' loading="' + (i < 2 ? 'eager' : 'lazy') + '"' +
      ' draggable="false">' +
      '</div>';
  }).join('');

  var dotsEl = document.getElementById('zoomDots');
  if (_zoomTotal > 1) {
    dotsEl.style.display = 'flex';
    dotsEl.innerHTML = _zoomImages.map(function(_, i) {
      return '<button class="zoom-modal__dot' + (i === _zoomIndex ? ' is-active' : '') +
             '" onclick="zoomGoTo(' + i + ')" aria-label="Image ' + (i+1) + '"></button>';
    }).join('');
  } else {
    dotsEl.style.display = 'none';
    dotsEl.innerHTML = '';
  }

  var box = document.getElementById('zoomBox');
  box.classList.toggle('zoom-modal--single', _zoomTotal === 1);

  var nameEl = document.getElementById('zoomProductName');
  if (nameEl) nameEl.textContent = _cleanTitle(prod.title || '');

  track.style.transition = 'none';
  track.style.transform  = 'translateX(-' + (_zoomIndex * 100) + '%)';

  var modal = document.getElementById('zoomModal');
  modal.classList.remove('is-closing');
  modal.classList.add('is-open');
  _bsbLockScroll();

  requestAnimationFrame(function() {
    requestAnimationFrame(function() {
      var t = document.getElementById('zoomTrack');
      if (t) t.style.transition = '';
    });
  });

  _zoomUpdateCounter();
  _zoomUpdateArrows();
  _zoomAttachInteraction();

  setTimeout(function() {
    var btn = document.getElementById('zoomClose');
    if (btn) btn.focus();
  }, 80);
}

function closeZoom() {
  var modal = document.getElementById('zoomModal');
  if (!modal || !modal.classList.contains('is-open')) return;
  modal.classList.add('is-closing');
  setTimeout(function() {
    modal.classList.remove('is-open', 'is-closing');
    _bsbUnlockScroll();
    var t = document.getElementById('zoomTrack');
    if (t) t.innerHTML = '';
    _zoomImages = [];
    _zoomTotal  = 0;
    _zoomCombos = null;
  }, 240);
}

function zoomGoTo(idx) {
  if (idx < 0 || idx >= _zoomTotal) return;
  _zoomIndex = idx;

  document.querySelectorAll('.zoom-modal__slide img.is-zoomed').forEach(function(img) {
    img.classList.remove('is-zoomed');
  });

  var track = document.getElementById('zoomTrack');
  if (track) track.style.transform = 'translateX(-' + (_zoomIndex * 100) + '%)';

  _zoomUpdateCounter();
  _zoomUpdateArrows();
  _zoomUpdateDots();
}

function _zoomUpdateCounter() {
  var el = document.getElementById('zoomImgCount');
  if (el) el.textContent = (_zoomIndex + 1) + ' / ' + _zoomTotal;
  /* Combo gallery mode — keep the header title in sync with the current slide */
  if (_zoomCombos && _zoomCombos[_zoomIndex]) {
    var c      = _zoomCombos[_zoomIndex];
    var nameEl = document.getElementById('zoomProductName');
    if (nameEl) nameEl.textContent = 'Combination ' + c.index + ' — ' + _cleanTitle(c.strap.title) + ' + ' + _cleanTitle(c.buckle.title);
  }
}
function _zoomUpdateArrows() {
  var prev = document.getElementById('zoomPrev');
  var next = document.getElementById('zoomNext');
  if (prev) prev.disabled = (_zoomIndex === 0);
  if (next) next.disabled = (_zoomIndex === _zoomTotal - 1);
}
function _zoomUpdateDots() {
  document.querySelectorAll('#zoomDots .zoom-modal__dot').forEach(function(dot, i) {
    dot.classList.toggle('is-active', i === _zoomIndex);
  });
}

function _zoomAttachInteraction() {
  var oldVp = document.getElementById('zoomViewport');
  if (!oldVp) return;
  var newVp = oldVp.cloneNode(true);
  oldVp.parentNode.replaceChild(newVp, oldVp);

  var vp = document.getElementById('zoomViewport');

  vp.addEventListener('touchstart', function(e) {
    if (e.touches.length !== 1) return;
    _zoomTouchX = e.touches[0].clientX;
    _zoomTouchY = e.touches[0].clientY;
  }, { passive: true });

  vp.addEventListener('touchend', function(e) {
    var dx = e.changedTouches[0].clientX - _zoomTouchX;
    var dy = e.changedTouches[0].clientY - _zoomTouchY;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 44) {
      dx < 0 ? zoomGoTo(_zoomIndex + 1) : zoomGoTo(_zoomIndex - 1);
    }
  }, { passive: true });

  vp.addEventListener('mousedown', function(e) {
    if (e.button !== 0) return;
    _zoomDragging  = true;
    _zoomJustDrag  = false;
    _zoomDragX     = e.clientX;
    _zoomDragDX    = 0;
  });
  vp.addEventListener('mousemove', function(e) {
    if (!_zoomDragging) return;
    _zoomDragDX = e.clientX - _zoomDragX;
  });
  var _endDrag = function() {
    if (!_zoomDragging) return;
    _zoomDragging = false;
    _zoomJustDrag = Math.abs(_zoomDragDX) > 10;
    if (Math.abs(_zoomDragDX) > 64) {
      _zoomDragDX < 0 ? zoomGoTo(_zoomIndex + 1) : zoomGoTo(_zoomIndex - 1);
    }
    _zoomDragDX = 0;
  };
  vp.addEventListener('mouseup',    _endDrag);
  vp.addEventListener('mouseleave', _endDrag);

  vp.addEventListener('click', function(e) {
    if (_zoomJustDrag) { _zoomJustDrag = false; return; }
    var img = e.target.closest('.zoom-modal__slide img');
    if (!img) return;
    var willZoom = !img.classList.contains('is-zoomed');
    document.querySelectorAll('.zoom-modal__slide img').forEach(function(i) {
      i.classList.remove('is-zoomed');
    });
    if (willZoom) img.classList.add('is-zoomed');
  });
}

/* Fixed zoom button wiring */
document.getElementById('zoomClose').addEventListener('click', closeZoom);
document.getElementById('zoomBackdrop').addEventListener('click', closeZoom);
document.getElementById('zoomPrev').addEventListener('click', function() { zoomGoTo(_zoomIndex - 1); });
document.getElementById('zoomNext').addEventListener('click', function() { zoomGoTo(_zoomIndex + 1); });

/* ══════════════════════════════════════════════════════════
   21. AUTO-SCROLL ENGINE
══════════════════════════════════════════════════════════ */
/**
 * _smoothScrollTo — rAF-based smooth scroll with cubic ease-in-out.
 *
 * Calculates the start position once before the animation loop, then drives
 * position purely with math each frame — no incremental reads that could
 * drift under competing work. Cubic ease-in-out (slow → fast → slow) gives
 * the same natural feel as the reference design system.
 *
 * Cancellation: starting a new scroll cancels any in-progress rAF, so only
 * the most-recently-requested scroll ever plays out.
 *
 * @param {number} targetY  - Absolute page Y to scroll to (px, clamped to ≥0)
 * @param {number} duration - Animation duration in ms (default 1200)
 */
function _smoothScrollTo(targetY, duration) {
  if (_smoothScrollTo._raf) {
    cancelAnimationFrame(_smoothScrollTo._raf);
    _smoothScrollTo._raf = null;
  }
  /* CSS scroll-behavior:smooth intercepts every window.scrollTo() inside the rAF loop
     and creates competing browser animations — causing stutter. Force 'auto' for the
     duration of this animation, then restore. */
  document.documentElement.style.scrollBehavior = 'auto';
  /* Cancel any iOS Safari momentum scroll in progress. Snapping to the current
     position first stops iOS from overriding the scrollTo() calls below with its
     own inertia (this is what made the scroll stop half-way on iPhone). */
  var _curY = window.pageYOffset || window.scrollY;
  window.scrollTo(0, _curY);
  duration = (duration != null) ? duration : 900;
  var startY    = window.pageYOffset || window.scrollY;
  /* targetY may be a number, or a function that returns the target Y. A function is
     re-evaluated every frame, so layout that shifts while we scroll (grid rendering,
     images loading, the sticky bar switching to fixed) cannot leave us short. */
  var live      = (typeof targetY === 'function');
  var endY      = Math.max(0, live ? targetY() : targetY);
  var startTime = null;

  function step(currentTime) {
    if (startTime === null) startTime = currentTime;
    var elapsed  = currentTime - startTime;
    var progress = Math.min(elapsed / duration, 1);
    /* Quintic ease-out: instant start, smooth deceleration to stop */
    var ease = 1 - Math.pow(1 - progress, 5);
    if (live) endY = Math.max(0, targetY());
    window.scrollTo(0, startY + (endY - startY) * ease);
    if (elapsed < duration) {
      _smoothScrollTo._raf = requestAnimationFrame(step);
    } else {
      window.scrollTo(0, endY);
      _smoothScrollTo._raf = null;
      document.documentElement.style.scrollBehavior = '';
    }
  }
  _smoothScrollTo._raf = requestAnimationFrame(step);
}
_smoothScrollTo._raf = null;

/* Strip leading size prefix like `1.25" ` from a product title for display. 
function _cleanTitle(t) {
  if (typeof t !== 'string') return t;
  var parts = t.split('" ');
  return parts.length > 1 ? parts[parts.length - 1].trim() : t;
}*/
/* Strip size like `1.25"` or `1.5"` from a product title, keeping everything else (e.g. XL). */
function _cleanTitle(t) {
  if (typeof t !== 'string') return t;
  return t.replace(/\d+\.?\d*"\s*/g, '').trim();
}

/* Single cancellable timer for all section-scroll calls.
   Ensures a later scrollToSection() always wins over an earlier one,
   and lets callers cancel a pending scroll before scheduling a new one. */
var _scrollTid = null;

/**
 * Schedule a smooth scroll to a section by element ID.
 *
 * @param {string} id       - Element ID to scroll to
 * @param {number} delay    - ms to wait before starting (default 300)
 *                           Use ~300ms for forward unlock (waits for expand animation),
 *                           ~100ms for backward collapse (DOM shrinks before scroll).
 * @param {number} duration - Scroll animation duration in ms (default 1200)
 */
function scrollToSection(id, delay, duration, offset) {
  /* Cancel any scroll that was scheduled but hasn't fired yet */
  if (_scrollTid) { clearTimeout(_scrollTid); _scrollTid = null; }
  _scrollTid = setTimeout(function () {
    _scrollTid = null;
    var el = document.getElementById(id);
    if (!el) return;
    /* Use caller-supplied offset; default = 90px to clear the sticky progress bar */
    var _off = (offset != null) ? offset : 90;
    var top = el.getBoundingClientRect().top + (window.pageYOffset || window.scrollY) - _off;
    _smoothScrollTo(Math.max(0, top), duration);
  }, delay != null ? delay : 300);
}

/* ── Per-section scroll helpers ─────────────────────────────────────────────
   Each function scrolls to exactly one section. To fine-tune where the
   viewport lands after a scroll, change ONLY the OFFSET value inside that
   function (in pixels from the viewport top to the section header).
────────────────────────────────────────────────────────────────────────────── */

/* Measures the sticky bar height at the moment of scrolling.
   Returns 0 when the bar is not active so the gap value alone applies. */
function _stickyBarH() {
  var bar = document.getElementById('bsbStickyBar');
  return (bar && bar.classList.contains('is-sticky')) ? (bar.offsetHeight || 0) : 0;
}

function scrollToStrapSection(delay) {
  var OFFSET        = 130; /* desktop: breathing-room gap below the sticky bar (px) */
  var MOBILE_OFFSET = 100; /* mobile:  same — change this value to tune on mobile  */
  if (_scrollTid) { clearTimeout(_scrollTid); _scrollTid = null; }
  _scrollTid = setTimeout(function() {
    _scrollTid = null;
    var el = document.getElementById('stepCard2');
    if (!el) return;
    var gap = window.innerWidth < 768 ? MOBILE_OFFSET : OFFSET;
    _smoothScrollTo(Math.max(0, el.getBoundingClientRect().top + (window.pageYOffset || window.scrollY) - _stickyBarH() - gap));
  }, delay != null ? delay : 300);
}

/* ── Dedicated mobile-only scroll after size click ──────────────────────────
   Uses native smooth scroll (no iOS snap) — snaps break Android scroll.
   Calculates target fresh inside the timer so layout is settled after unlock.
   Remove the alert() line once scroll position is confirmed correct.        */
var _mobileScrollTid = null;
function scrollToStrapSectionMobileOnly(delay) {
  if (_mobileScrollTid) { clearTimeout(_mobileScrollTid); _mobileScrollTid = null; }
  _mobileScrollTid = setTimeout(function() {
    _mobileScrollTid = null;
    if (window.innerWidth >= 768) return;
    var MOBILE_OFFSET = 0; /* 0 = card lands right below the sticky bar */
    var el      = document.getElementById('stepCard2');
    var bar     = document.getElementById('bsbStickyBar');
    var barH    = (bar && bar.classList.contains('is-sticky')) ? bar.getBoundingClientRect().height : 0;
    var scrollY = window.pageYOffset || window.scrollY;
    var elTop   = el ? el.getBoundingClientRect().top : 0;
    var target  = Math.max(0, elTop + scrollY - barH - MOBILE_OFFSET);
    if (!el) return;
    window.scrollTo({ top: target, behavior: 'smooth' });
  }, delay != null ? delay : 300);
}

/* ── Dedicated desktop-only scroll after size click ──────────────────────── */
var _desktopScrollTid = null;
function scrollToStrapSectionDesktopOnly(delay) {
  var BREATHING_ROOM = 16;
  if (_desktopScrollTid) { clearTimeout(_desktopScrollTid); _desktopScrollTid = null; }
  _desktopScrollTid = setTimeout(function() {
    _desktopScrollTid = null;
    if (window.innerWidth < 768) return;
    var el = document.getElementById('stepCard2');
    if (!el) return;
    var bar = document.getElementById('bsbStickyBar');
    var barH = bar ? bar.offsetHeight : 56;
    var target = Math.max(0, el.getBoundingClientRect().top + (window.pageYOffset || window.scrollY) - barH - BREATHING_ROOM);
    _smoothScrollTo(target);
  }, delay != null ? delay : 300);
}

function scrollToStraps(delay) {
  var OFFSET        = 130; /* desktop: breathing-room gap below the sticky bar (px) */
  var MOBILE_OFFSET = 100; /* mobile:  same — change this value to tune on mobile  */
  if (_scrollTid) { clearTimeout(_scrollTid); _scrollTid = null; }
  _scrollTid = setTimeout(function() {
    _scrollTid = null;
    var el = document.getElementById('filterBarStraps');
    if (!el) return;
    var gap = window.innerWidth < 768 ? MOBILE_OFFSET : OFFSET;
    _smoothScrollTo(Math.max(0, el.getBoundingClientRect().top + (window.pageYOffset || window.scrollY) - _stickyBarH() - gap));
  }, delay != null ? delay : 100);
}

function scrollToBuckleFilter(delay) {
  var OFFSET        = 130; /* desktop: breathing-room gap below the sticky bar (px) */
  var MOBILE_OFFSET = 100; /* mobile:  same — change this value to tune on mobile  */
  if (_scrollTid) { clearTimeout(_scrollTid); _scrollTid = null; }
  _scrollTid = setTimeout(function() {
    _scrollTid = null;
    var el = document.getElementById('filterBarBuckles');
    if (!el) return;
    var gap = window.innerWidth < 768 ? MOBILE_OFFSET : OFFSET;
    _smoothScrollTo(Math.max(0, el.getBoundingClientRect().top + (window.pageYOffset || window.scrollY) - _stickyBarH() - gap));
  }, delay != null ? delay : 100);
}

function scrollToBuckleSection(delay) {
  var OFFSET        = 15; /* desktop: breathing-room gap below the sticky bar (px) */
  var MOBILE_OFFSET = 20; /* mobile:  same — change this value to tune on mobile  */
  if (_scrollTid) { clearTimeout(_scrollTid); _scrollTid = null; }
  _scrollTid = setTimeout(function() {
    _scrollTid = null;
    var el = document.getElementById('stepCard3');
    if (!el) return;
    var gap = window.innerWidth < 768 ? MOBILE_OFFSET : OFFSET;
    _smoothScrollTo(Math.max(0, el.getBoundingClientRect().top + (window.pageYOffset || window.scrollY) - _stickyBarH() - gap));
  }, delay != null ? delay : 300);
}

function scrollToFinalSection(delay) {
  var OFFSET        = 15; /* desktop: breathing-room gap below the sticky bar (px) */
  var MOBILE_OFFSET = 20; /* mobile:  same — change this value to tune on mobile  */
  if (_scrollTid) { clearTimeout(_scrollTid); _scrollTid = null; }
  _scrollTid = setTimeout(function() {
    _scrollTid = null;
    var el = document.getElementById('stepCard4');
    if (!el) return;
    var gap = window.innerWidth < 768 ? MOBILE_OFFSET : OFFSET;
    _smoothScrollTo(Math.max(0, el.getBoundingClientRect().top + (window.pageYOffset || window.scrollY) - _stickyBarH() - gap));
  }, delay != null ? delay : 150);
}

function handleAutoScroll(action, wasAlreadyCompleted) {
  var sc = filterManagers.strap.selectedStraps.length;
  var bc = filterManagers.buckle.selectedBuckles.length;

  switch (action) {
    /* ── Forward unlock (expand animation fires first, then scroll) ── */
    case 'add-strap':
      if (sc === MAX_STRAPS) {
        if (upgradeModalOpen()) break;
        /* 300ms gives the buckle card time to fully expand before measuring position */
        if (bc >= MAX_BUCKLES) { scrollToFinalSection(300); } else { scrollToBuckleSection(300); }
      }
      break;
    case 'add-buckle':
      if (upgradeModalOpen()) break;
      /* 150ms first-time (step 4 expands), 100ms re-completion (already rendered once) */
      if (bc === MAX_BUCKLES) scrollToFinalSection(wasAlreadyCompleted ? 100 : 150);
      break;
    case 'reset':
      scrollToStraps(150);
      break;
    /* ── Backward collapse (DOM already shrinks before scroll, 100ms enough) ── */
    case 'remove-strap':
      scrollToStraps(100);
      break;
    case 'remove-buckle':
      /* If the buckle section is locked (straps not yet complete), guide the
         user to the straps step first — scrolling to a locked section is useless. */
      if (document.getElementById('stepCard3')?.classList.contains('is-locked')) {
        scrollToStraps(100);
      } else {
        scrollToBuckleSection(100);
      }
      break;
  }
}

/* ══════════════════════════════════════════════════════════
   22. PRODUCT-FORM SUBMISSION GUARD
   Blocks Dawn's product-form.js from adding the default
   variant whenever any form on the page is submitted.
══════════════════════════════════════════════════════════ */
(function blockProductFormSubmit() {
  function interceptForms() {
    document.querySelectorAll('form[action*="/cart/add"], product-form form').forEach(function(form) {
      if (form.dataset.bsbGuarded) return;
      form.dataset.bsbGuarded = '1';
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        console.warn('[BSB] Blocked product form submit — cart is managed by Belt Box Set Builder');
      }, true);
    });
  }
  interceptForms();
  document.addEventListener('DOMContentLoaded', interceptForms);
  var _formObserver = new MutationObserver(interceptForms);
  _formObserver.observe(document.body, { childList: true, subtree: true });
})();

/* ══════════════════════════════════════════════════════════
   23. GLOBAL EVENT LISTENERS
══════════════════════════════════════════════════════════ */
/* ── Grid scroll fade — passive scroll + resize listeners ── */
window.addEventListener('scroll', _scheduleGridFade, { passive: true });
window.addEventListener('resize', _scheduleGridFade, { passive: true });


/* Also update fades whenever the grid content grows (filters / load-more) */
(function() {
  if (typeof ResizeObserver === 'undefined') return;
  var _rObs = new ResizeObserver(_scheduleGridFade);
  ['strapsGridWrap', 'bucklesGridWrap'].forEach(function(id) {
    var el = document.getElementById(id);
    if (el) _rObs.observe(el);
  });
})();

document.addEventListener('click', e => {
  const upgrade = document.getElementById('upgradeModal');
  if (upgrade && e.target === upgrade) declineUpgrade();
  const reset = document.getElementById('bsbResetModal');
  if (reset && e.target === reset) cancelWidthChange();
  document.querySelectorAll('.card-popup.is-open').forEach(p => p.classList.remove('is-open'));

  /* Close inline strap tooltip when clicking outside it or its trigger buttons */
  if (!e.target.closest('.strap-tooltip') &&
      !e.target.closest('.product-card__info-btn') &&
      !e.target.closest('#bsbStrapTipBackdrop')) {
    document.querySelectorAll('.strap-tooltip.is-open').forEach(function(el) {
      var m = el.id.match(/^stip-(.+)$/);
      if (m) closeStrapInfo(m[1]); else el.classList.remove('is-open');
    });
  }
});
document.addEventListener('keydown', e => {
  if (e.key === 'Escape') {
    closeZoom(); closeLightbox(); cancelWidthChange(); declineUpgrade();
    closePremiumModal(); closeMixWidthsModal(); closeKlarnaModal();
    /* close filter drawers */
    var _fp = document.getElementById('filterPanel');
    if (_fp && _fp.classList.contains('is-open')) toggleFilter();
    var _fpb = document.getElementById('filterPanelBuckles');
    if (_fpb && _fpb.classList.contains('is-open')) toggleFilterBuckles();
    document.querySelectorAll('.strap-tooltip.is-open').forEach(function(el) {
      var m = el.id.match(/^stip-(.+)$/);
      if (m) closeStrapInfo(m[1]); else el.classList.remove('is-open');
    });
  }
  var zm = document.getElementById('zoomModal');
  if (zm && zm.classList.contains('is-open')) {
    if (e.key === 'ArrowLeft')  { e.preventDefault(); zoomGoTo(_zoomIndex - 1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); zoomGoTo(_zoomIndex + 1); }
  }
});

/* ══════════════════════════════════════════════════════════
   INIT — run price display immediately so the correct
   formatted price shows from the very first render.
   (DynamicPrice is normally called via updateAddonSystem on
   selection change; this seeds the initial display.)
══════════════════════════════════════════════════════════ */
DynamicPrice();

/* Preload all product images in background so they feel instant when revealed */
(function _bsbPreloadImages() {
  var seen = new Set();
  ALL_PRODUCTS.forEach(function(p) {
    if (p.image && !seen.has(p.image)) {
      seen.add(p.image);
      var img = new Image();
      img.src = p.image;
    }
  });
})();


/* ══════════════════════════════════════════════════════════
   FINAL ATC TABS — switch active tab + panel
══════════════════════════════════════════════════════════ */
function bsbTabSwitch(btn, panelId) {
  var nav = btn.closest('.final-atc__tab-nav');
  if (!nav) return;
  nav.querySelectorAll('.final-atc__tab-btn').forEach(function(b) {
    b.classList.remove('is-active');
  });
  btn.classList.add('is-active');
  var body = nav.nextElementSibling;
  if (!body) return;
  body.querySelectorAll('.final-atc__tab-panel').forEach(function(p) {
    p.classList.remove('is-active');
  });
  var panel = document.getElementById(panelId);
  if (panel) panel.classList.add('is-active');
}

/* Set up ATC tooltip listeners on page load so the tooltip
   is ready even before the user has made any selection. */
(function() {
  function _bsbBootTooltip() { _bsbInitTooltip(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _bsbBootTooltip);
  } else {
    _bsbBootTooltip();
  }
})();

/* ══════════════════════════════════════════════════════════
   BUCKLE AJAX LOADER
   Shopify Liquid caps un-paginated collection loops at 50.
   The straps section already uses the one allowed paginate
   block per page. This loader fetches ALL buckles from the
   public /collections/{handle}/products.json endpoint when
   the collection has > 50 products, then replaces the
   Liquid-rendered cards with a complete set.
══════════════════════════════════════════════════════════ */

var _BUCKLE_STYLE_DESC = {
  'classic': '<p>The Classic Buckle is our timeless, full-faced buckle design that\'s been a staple of style for generations.</p><p> Its clean lines and balanced proportions make it one of the most versatile buckles we offer.</p><p>Pairing effortlessly with everything from business attire to your favorite pair of jeans.</p><p> The Classic is the foundation of the Anson collection.</p>',
  'classic with curve': '<p>The Classic Buckle with a Curve builds upon one of our most timeless buckle designs with softer lines and a more refined profile.</p><p>While retaining the full-faced styling that has made the Classic buckle a staple for decades, this version features graceful curves along both sides, creating a sleek, elegant silhouette that\'s equally at home with casual, business, or formal attire.</p><p>Beyond its distinctive appearance, the curved profile offers a practical advantage. By eliminating the sharper corners found on traditional rectangular buckles, the Classic with a Curve sits more comfortably against the body.</p><p>Making it an excellent choice for anyone who finds their buckle pressing into their stomach when sitting or bending throughout the day.</p><p> The result is a buckle that\'s not only beautiful, but exceptionally comfortable to wear.</p>',
  'compact classic buckle': '<p>The Compact Classic Buckle was born from customer feedback and designed to solve a common problem.</p><p>Maintaining the clean, full-faced styling of our Classic Buckle, the Compact Classic is approximately 30% smaller, making it ideal for those who prefer wearing their buckle slightly off-center to keep the front of their waist clear.</p><p>Originally inspired by customers who appendix carry, the Compact Classic has quickly become a favorite among auto detailers, mechanics, countertop installers, and anyone whose work or hobbies place them in frequent contact with vehicles, equipment, or finished surfaces.</p><p>By reducing the buckle\'s footprint, it minimizes rubbing and contact against objects in front of you while providing greater comfort and freedom of movement throughout the day.</p>',
  'traditional': '<p>The Traditional Buckle is our flagship buckle style and the design that helped define the Anson brand.</p><p> Inspired by the timeless look of a classic framed belt buckle, it was the first micro-adjustable, hole-less ratchet buckle in the world with an open-faced design—designed and introduced by Anson.</p><p>Its open-faced design showcases more of the strap, creating a timeless look that transitions effortlessly from casual wear to business attire.</p><p>Its distinctive silhouette became so synonymous with our brand that it even inspired the Anson logo... though many people don\'t notice the subtle resemblance at first glance.</p>',
  'traditional with curve': '<p>The Traditional Buckle with a Curve takes everything customers love about our flagship Traditional Buckle and adds a softer, more refined silhouette.</p><p>Inspired by the timeless look of a classic framed buckle, this design replaces the traditional squared profile with a graceful curve along one side, creating a more elegant appearance while maintaining the iconic open-faced design that made the Traditional buckle famous.</p><p>Perfect for those who appreciate classic styling with a modern touch, the subtle curved profile gives the buckle a more fluid, sophisticated look without sacrificing the versatility that has made the Traditional one of our most popular buckle styles.</p><p>Whether paired with business attire or your favorite casual outfit, the Traditional with a Curve offers a unique take on an Anson classic.</p>',
  'golf': '<p>Designed by Anson for golfers who appreciate both performance and craftsmanship, the Golf Buckle celebrates one of the sports our belts are perfectly suited for.</p><p>Thanks to our micro-adjustable system, you can enjoy the same perfect fit from the 1st tee to the 18th green, adjusting your belt in precise ¼-inch increments as needed throughout the round.</p><p>What truly sets the Golf Buckle apart is its distinctive golf ball dimple pattern. Each buckle features beautifully detailed dimples that create exceptional texture.</p><p>The showcases showcases a full golf ball-inspired design with remarkable depth, creating an almost three-dimensional appearance that\'s sure to stand out.</p>',
  'onyx': '<p>The Onyx Buckle combines timeless styling with contemporary design, creating a look that\'s both refined and distinctive.</p></p> At the heart of every Onyx Buckle is a smooth resin inlay, available in Black or White, creating a striking sense of depth and contrast unlike any other buckle in the Anson collection.</p><p>Framed by beautifully finished metal, the result is a sophisticated buckle that looks equally at home with business attire, formal occasions, or elevated everyday wear.</p><p>The Black Onyx inlay pairs effortlessly with black and darker straps for a sleek, coordinated appearance, while the White Onyx offers crisp contrast against darker leathers or blends beautifully with white straps for a clean, modern look.</p>',
  'usa flag embossed': '<p>The USA Flag Buckle proudly showcases one of America\'s most recognizable symbols through exceptional craftsmanship and attention to detail.</p><p>Our Embossed USA Flag Buckle features a beautifully detailed American flag pressed directly into the face of the buckle, creating rich texture and depth while maintaining a clean, understated appearance.</p><p>The USA Flag Buckle pairs perfectly with everything from everyday casual wear to special patriotic occasions.</p>',
  'usa flag full': '<p>The USA Flag Buckle proudly showcases one of America\'s most recognizable symbols through exceptional craftsmanship and attention to detail.</p><p>Our Full Color USA Flag Buckle showcases a vibrant, high-definition American flag protected beneath a crystal-clear resin inlay.</p><p>The smooth resin surface enhances the depth and brilliance of the artwork while protecting it from everyday wear, creating a striking buckle that\'s built to last.</p><p>The USA Flag Buckle pairs perfectly with everything from everyday casual wear to special patriotic occasions.</p>',
  'full': '<p>The USA Flag Buckle proudly showcases one of America\'s most recognizable symbols through exceptional craftsmanship and attention to detail.</p><p>Our Full Color USA Flag Buckle showcases a vibrant, high-definition American flag protected beneath a crystal-clear resin inlay.</p><p>The smooth resin surface enhances the depth and brilliance of the artwork while protecting it from everyday wear, creating a striking buckle that\'s built to last.</p><p>The USA Flag Buckle pairs perfectly with everything from everyday casual wear to special patriotic occasions.</p>',
  'ecu': '<p>The ECU Buckle was created as a tribute to the tradition, pride, and unmistakable spirit of East Carolina University. It features the officially licensed ECU Jolly Roger beautifully embossed into our Classic full-faced buckle.</p><p>This design celebrates one of college athletics\' most recognizable symbols with exceptional craftsmanship and attention to detail.</p><p>Whether you\'re an ECU alumnus, a lifelong Pirate fan, or simply appreciate a bold, distinctive design, the ECU Buckle is a timeless way to show your Pirate pride.</p><p>It\'s equally at home on game day, around town, or anywhere you want to carry a piece of East Carolina with you.</p>',
  'wave': '<p>The Wave Buckle is a celebration of the Carolina coast and the lifestyle that inspired Anson from the very beginning. It was created by Anson co-founder David Ferree and brought to life through the incredible craftsmanship of our manufacturing partners.</p><p>The Wave Buckle transforms the movement and energy of the ocean into a timeless, wearable design. Inspired by the waters that surround North Carolina\'s Crystal Coast, the flowing contours of the buckle capture the rhythm of the waves with a clean, sculpted profile that\'s both elegant and understated.</p><p>Every curve was carefully considered to create a buckle that\'s as comfortable to wear as it is beautiful to look at. The Wave Buckle represents Anson\'s continued commitment to innovation.</p><p>Pushing the boundaries of what\'s possible in the world of micro-adjustable belts while remaining true to the timeless craftsmanship our customers expect.</p><p>Whether you\'re drawn to the coast, appreciate thoughtful design, or simply want something unlike anything else in your collection, the Wave Buckle is designed to make a lasting impression.</p>'
};

var _BUCKLE_COLOR_DESC = {
  'gold':               '<p>A rich, soft matte gold finish that offers timeless sophistication without excessive shine. Elegant enough for dress wear, yet versatile enough for everyday use.</p>',
  'rose gold':          '<p>A refined matte rose gold finish that adds a modern touch without being overpowering. A perfect complement to rose gold watches, jewelry, and accessories.</p>',
  'antiqued gold':      '<p>A warm, vintage-inspired finish with rich depth and character. Available in both widths, each with its own personality.</p><p>1.25": A slightly glossier finish that transitions effortlessly from business attire to formal occasions.</p><p>1.5": A softer matte finish with warm, vintage-inspired character that pairs beautifully with casual leather straps and rich brown tones.</p>',
  'silver':             '<p>A classic soft matte silver finish that never goes out of style. Bright, clean, and incredibly versatile, it\'s a timeless choice for both casual and dress wear.</p>',
  'matte gunmetal':     '<p>Our Matte Gunmetal features a soft matte grey finish that delivers a clean, understated look.</p><p>Modern and incredibly versatile, it pairs effortlessly with virtually every strap in our collection, making it an excellent choice for everyday wear.</p>',
  'smoked gunmetal':    '<p>Our Smoked Gunmetal features a deep, smoked finish with a rich, glossy appearance.</p><p>Elegant and sophisticated, it\'s an outstanding choice for business attire, formal occasions, or anyone looking for a darker, more refined alternative to silver.</p>',
  'formal gunmetal':    '<p>Designed with dress wear in mind, Formal Gunmetal features a deep smoked-black frame with subtle silver accent lines and a lighter gunmetal face for added depth and contrast.</p><p>The refined, glossy finish creates an elevated look that\'s perfect for business attire, weddings, and other formal occasions.</p>',
  'gunmetal':           '<p>Our signature finish and the most popular buckle in the Anson lineup. This soft matte gunmetal grey pairs effortlessly with virtually every strap in our collection, making it the perfect everyday buckle for both casual and formal occasions.</p>',
  'black':              '<p>Finished in a smooth matte black, this buckle delivers a clean, understated look that pairs especially well with black, grey, and modern monochromatic styles.</p>',
  'nickel free graphite': '<p>Available exclusively in the Traditional style, our Nickel Free Graphite provides nickel free and hypoallergenic construction in a rich, dark grey finish.</p><p>Darker than our Gunmetal, Nickel Free Graphite offers a sophisticated, understated alternative to our shiny Nickel Free finish.</p>',
  'nickel free':        '<p>Available exclusively in the Traditional style, our Nickel Free features a brilliant, chrome-like finish while remaining completely nickel free and hypoallergenic, making them an excellent choice for those with nickel sensitivities.</p>'
};

function _buildBuckleTooltipHTML(tags) {
  var styleHtml = '', colorHtml = '';
  (tags || []).forEach(function(tag) {
    var lower = (tag + '').toLowerCase();
    if (lower.indexOf('buckle-info-') === -1) return;
    var material = lower.split('buckle-info-')[1].replace(/-/g, ' ').trim();
    if (!styleHtml && _BUCKLE_STYLE_DESC[material]) styleHtml = _BUCKLE_STYLE_DESC[material];
    if (!colorHtml && _BUCKLE_COLOR_DESC[material]) colorHtml = _BUCKLE_COLOR_DESC[material];
  });
  var out = '';
  if (styleHtml) out += '<strong class="strap-tooltip__section-label">STYLE</strong>' + styleHtml;
  if (colorHtml) out += '<strong class="strap-tooltip__section-label">FINISH</strong>' + colorHtml;
  return out;
}

function _bsbImgSized(src, w) {
  if (!src) return '';
  return src.replace(/(\.\w+)(\?|$)/, '_' + w + 'x$1$2');
}

function _buildBuckleCardHTML(p) {
  var pid      = 'b' + p.id;
  var tags     = p.tags || [];
  var tagsEsc  = tags.join(',').replace(/&/g,'&amp;').replace(/"/g,'&quot;');
  var size     = '';
  tags.forEach(function(t) {
    if (!size && (t+'').toLowerCase().indexOf('size_') !== -1)
      size = (t+'').toLowerCase().split('size_')[1].replace(/-/g, '.');
  });
  var featSrc  = (p.featured_image && p.featured_image.src) || (p.images && p.images[0] && p.images[0].src) || '';
  var img400   = _bsbImgSized(featSrc, 400);
  var allImgs  = '[' + (p.images || []).map(function(i){ return '"' + _bsbImgSized(i.src, 800) + '"'; }).join(',') + ']';
  var price    = Math.round(parseFloat((p.variants && p.variants[0] && p.variants[0].price) || '0') * 100);
  var _titleRaw = p.title || '';
  var title    = _titleRaw.replace(/&/g,'&amp;').replace(/"/g,'&quot;'); /* full title for data-title */
  var titleDisp = _cleanTitle(_titleRaw).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); /* stripped for display */
  var sku      = ((p.variants && p.variants[0] && p.variants[0].sku) || '').replace(/"/g,'&quot;');
  var tipHtml  = _buildBuckleTooltipHTML(tags);
  var infoBtn  = tipHtml
    ? '<button class="product-card__info-btn" data-stop onclick="event.stopPropagation();openStrapInfo(\'' + pid + '\')" title="About this buckle" aria-label="Buckle info"><i class="fa-solid fa-circle-info text-[#6B7280] text-xs" aria-hidden="true"></i></button>'
    : '';
  var imgTag   = img400
    ? '<img src="' + img400 + '" alt="' + titleDisp + '" loading="lazy">'
    : '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#999;font-size:12px;">No Image</div>';

  return '<div class="product-card" id="pc-buckle-' + pid + '" style="display:none"' +
    ' data-product-id="' + p.id + '" data-product-type="buckle" data-size="' + size + '"' +
    ' data-tags="' + tagsEsc + '" data-image="' + img400 + '"' +
    ' data-images="' + allImgs.replace(/"/g,'&quot;') + '"' +
    ' data-price="' + price + '" data-is-addon="false"' +
    ' data-title="' + title + '" data-handle="' + (p.handle||'') + '" data-sku="' + sku + '"' +
    ' onclick="filterManagers.buckle.onCardClick(event,\'' + pid + '\')">' +
    '<div class="product-card__check"><svg viewBox="0 0 13 10"><polyline points="1.5 5 5 8.5 11.5 1.5"/></svg></div>' +
    '<div class="product-card__dots"></div>' +
    '<div class="product-card__zoom" data-stop onclick="event.stopPropagation();openZoom(\'' + pid + '\')" title="View images"><svg viewBox="0 0 20 20"><circle cx="8.5" cy="8.5" r="5"/><line x1="13" y1="13" x2="18" y2="18"/></svg></div>' +
    infoBtn +
    '<div class="strap-tooltip" id="stip-' + pid + '" role="dialog" aria-modal="true" onclick="event.stopPropagation()">' +
      '<button class="strap-tooltip__close" onclick="event.stopPropagation();closeStrapInfo(\'' + pid + '\')" aria-label="Close"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>' +
      '<p class="strap-tooltip__title">' + titleDisp + '</p>' +
      '<div class="strap-tooltip__body">' + tipHtml + '</div>' +
    '</div>' +
    '<div class="product-card__img">' + imgTag + '</div>' +
    '<div class="product-card__info"><p class="product-card__name">' + titleDisp + '</p></div>' +
    '<div class="product-card__footer">' +
      '<button class="product-card__add-btn" id="add-btn-' + pid + '" aria-label="Add buckle">ADD</button>' +
      '<div class="product-card__stepper" id="stepper-' + pid + '" style="display:none;">' +
        '<button type="button" class="product-card__stepper-btn product-card__stepper-btn--minus" data-stop' +
          ' onclick="event.stopPropagation();filterManagers.buckle.stepperRemove(event,\'' + pid + '\')" aria-label="Remove one">−</button>' +
        '<span class="product-card__stepper-count" id="stepper-count-' + pid + '">1</span>' +
        '<button type="button" class="product-card__stepper-btn product-card__stepper-btn--plus" data-stop' +
          ' onclick="event.stopPropagation();filterManagers.buckle.stepperAdd(event,\'' + pid + '\')" aria-label="Add another">+</button>' +
      '</div>' +
    '</div>' +
  '</div>';
}

async function _loadAllBucklesFromAPI() {
  var d      = window.BSB_DATA || {};
  var handle = d.BUCKLES_HANDLE;
  var grid   = document.getElementById('productsGridBuckles');
  if (!handle || !grid) return;

  var all = [], page = 1;
  try {
    while (true) {
      var res  = await fetch('/collections/' + handle + '/products.json?limit=250&page=' + page);
      if (!res.ok) break;
      var data = await res.json();
      var prods = data.products || [];
      all = all.concat(prods);
      if (prods.length < 250) break;
      page++;
    }
  } catch(e) {
    console.warn('[BSB] Buckle AJAX load failed — showing Liquid-rendered first 50:', e);
    return;
  }
  if (!all.length) return;
  /* Replace Liquid-rendered cards (capped at 50) with the full API set */
  grid.innerHTML = all.map(_buildBuckleCardHTML).join('');
}

(function() {
  function _startBuckleLoad() { _loadAllBucklesFromAPI(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _startBuckleLoad);
  } else {
    _startBuckleLoad();
  }
})();

/* ── Tap on the review stars → scroll to the reviews ────────────────────────
   Stamped's own handler animates html+body with jQuery for 1s while the theme's
   `html { scroll-behavior: smooth }` starts a browser scroll for every step, so the
   two fight and the page crawls. Take the click over and use the same helper as the
   builder scrolls (live target, iOS-safe). */
document.addEventListener('click', function(e) {
  var badge = e.target.closest && e.target.closest('.stamped-product-reviews-badge, .stamped-badge, .stamped-badge-caption');
  if (!badge) return;
  var widget = document.getElementById('stamped-main-widget');
  if (!widget) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  _smoothScrollTo(function() {
    var bar = document.getElementById('bsbStickyBar');
    var barH = bar ? (bar.offsetHeight || 0) : 0;
    return widget.getBoundingClientRect().top + (window.pageYOffset || window.scrollY) - barH - 12;
  }, 700);
}, true);
